import { useState, useRef, useEffect } from 'react';
import { useStore } from '../services/store';
import { subscribeNodeGraphEvents } from '../services/nodeGraphEvents';
import { createCharacterUgcSnapshot, reattachCharacterReference, validateCharacterPortrait } from '../services/characterUgc';
import type { CharacterUgcCharacter, CharacterUgcRole } from '../services/characterUgc';
import type { CliProvider, AspectRatio, VideoQuality, PromptJob, ShotItem } from '../services/store';
import {
  TerminalSquare, BrainCircuit, Sparkles,
  Plus, Upload, ChevronRight, Trash2, Trash,
  PlayCircle, StopCircle, Settings2, FolderOpen,
  Lightbulb, FileText, Bot, Layers, Globe, ScrollText,
  Scissors, Zap, Image as ImageIcon, X, UserCircle2, EyeOff, Merge,
  Copy, Check, ExternalLink, RefreshCw, AlertCircle, CheckCircle2, Film, History, Clock
} from 'lucide-react';
import ScriptOutputPanel from './ScriptOutputPanel';
import ShotStatusPanel from './ShotStatusPanel';
import CharacterUgcProfiles from './CharacterUgcProfiles';
import FlowkitSkillsPanel from './FlowkitSkillsPanel';
import UgcBatchesQueueView from './UgcBatchesQueueView';
import MasterUgcV3Panel from './MasterUgcV3Panel';
import api from '../services/api';
import {
  DEFAULT_UGC_FLOWKIT_CONFIG, previewUgcFlowkitPipeline, startUgcFlowkitPipeline,
  formatGoogleFlowPrompts, getCurrentUgcFlowkitBatch, getExplicitlySelectedBatchId, getUgcFlowkitBatch,
  resumeUgcFlowkitBatch, setExplicitlySelectedBatchId, stopUgcFlowkitBatch, retryUgcFlowkitProductPlan, resumeUgcFlowkitProduct,
  type UgcFlowkitLaunchConfig, type UgcFlowkitSelectionPreview, type UgcFlowkitBatch
} from '../services/ugcFlowkit';

const CLI_PROVIDERS: { id: CliProvider; label: string; icon: any; color: string }[] = [
  { id: 'codex',  label: 'Codex CLI',  icon: TerminalSquare, color: '#7256c4' },
  { id: 'claude', label: 'Claude CLI', icon: BrainCircuit,   color: '#dc553c' },
  { id: 'gemini', label: 'Gemini CLI', icon: Sparkles,       color: '#258cc0' },
];

const TECHNIQUES = ['Cinematic', 'UGC', 'Tutorial', 'Review', 'Unboxing', 'Storytelling', 'POV', 'GRWM', 'ASMR', 'Trending'];
const MATERIALS  = ['Live Action', 'AI Generated', 'Animation', 'Mixed Media', 'Stock Footage', 'Screen Recording', 'Text Only'];
const CATEGORIES = ['Food & Beverage', 'Beauty & Skincare', 'Fashion & Apparel', 'Tech & Gadgets', 'Home & Living', 'Health & Wellness', 'Sports & Fitness', 'Education', 'Entertainment', 'General'];

const ASPECTS: AspectRatio[] = ['16:9', '9:16', '1:1', '4:3'];
const QUALITIES: VideoQuality[] = ['720p', '1080p', '4K'];
const LANGUAGES = ['en', 'vi', 'ja', 'zh', 'ko', 'fr', 'de', 'es'];
const LANG_LABELS: Record<string, string> = {
  en: 'English', vi: 'Tiếng Việt', ja: '日本語', zh: '中文',
  ko: '한국어', fr: 'Français', de: 'Deutsch', es: 'Español',
};

const EXAMPLE_IDEAS = [
  'Competition between rivals on Amazon marketplace',
  'Journey of a startup founder facing challenges',
  'Viking civilization in ancient times',
];

const CHARACTER_ENGINE_ERROR = 'Character UGC requires Flowkit or Flowboard native RPC. Select a supported engine before queuing.';

export default function MasterPromptTab() {
  const {
    mpCliProvider, setMpCliProvider,
    mpVideoMode, setMpVideoMode,
    mpLibraryMode, setMpLibraryMode,
    mpAspect, setMpAspect,
    mpQuality, setMpQuality,
    mpDuration, setMpDuration,
    mpLanguage, setMpLanguage,
    mpFolder, setMpFolder,
    mpJobs, addMpJob, removeMpJob, updateMpJob, clearMpJobs,
    shots, addShots, clearShots, processingJobId, setProcessingJobId,
    mpUgcMode, setMpUgcMode,
    mpCharacterUgc, setMpCharacterUgc,
    autoMode, setAutoMode, autoProductImageUrls, autoProductFileNames,
    setAutoProductImages, autoMerge, setAutoMerge, setAutoGeneratePending,
    mpEngine, setMpEngine,
    setUgcFlowkitBatchId, ugcFlowkitBatchId, setActiveTab,
    ugcMasterVersion, setUgcMasterVersion,
  } = useStore();

  // Engine is a single global switch — lock it while any shot is actively
  // generating/queued so a job's shots can never end up split across engines.
  const engineLocked = shots.some(s => s.status === 'generating' || s.status === 'uploading');

  const [ideas, setIdeas] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [technique, setTechnique] = useState('UGC');
  const [material, setMaterial] = useState('Live Action');
  const [category, setCategory] = useState('Food & Beverage');
  const [scriptJob, setScriptJob] = useState<PromptJob | null>(null);
  const [processingMsg, setProcessingMsg] = useState('');
  const [actionError, setActionError] = useState('');
  const [isQueuing, setIsQueuing] = useState(false);
  const [uploadingPortraits, setUploadingPortraits] = useState(false);
  const [ugcFlowkitOpen, setUgcFlowkitOpen] = useState(false);
  const [ugcFlowkitConfig, setUgcFlowkitConfig] = useState<UgcFlowkitLaunchConfig>(DEFAULT_UGC_FLOWKIT_CONFIG);
  const [ugcFlowkitStarting, setUgcFlowkitStarting] = useState(false);
  const [ugcFlowkitResult, setUgcFlowkitResult] = useState('');
  const [ugcFlowkitPreview, setUgcFlowkitPreview] = useState<{ key: string; value: UgcFlowkitSelectionPreview } | null>(null);
  const [ugcFlowkitSelectedProductIds, setUgcFlowkitSelectedProductIds] = useState<string[] | null>(null);
  const [v2Batch, setV2Batch] = useState<UgcFlowkitBatch | null>(null);
  const [v2BatchLoading, setV2BatchLoading] = useState(false);
  const [v2RightTab, setV2RightTab] = useState<'prompts' | 'batches'>('prompts');
  const [copiedPromptId, setCopiedPromptId] = useState<string | null>(null);

  const handlePromptCopy = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopiedPromptId(id);
    setTimeout(() => setCopiedPromptId(null), 2000);
  };
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef(false);
  const queuingRef = useRef(false);
  const processingRef = useRef(false);
  const portraitUploadsRef = useRef(0);
  const portraitRequestsRef = useRef<Record<CharacterUgcRole, number>>({ wife: 0, husband: 0 });
  const mountedRef = useRef(true);
  // The old per-job Character UGC editor required portrait uploads and manual
  // appearance/voice prompts. Product UGC now uses the shared roster only.
  const isCharacterUgc = false;
  const selectedSharedFamilyMode = technique === 'UGC' && mpUgcMode === 'character';
  const engineError = isCharacterUgc && mpEngine === 'antigravity' ? CHARACTER_ENGINE_ERROR : '';

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      portraitRequestsRef.current.wife++;
      portraitRequestsRef.current.husband++;
    };
  }, []);

  // ── Auto-process on mount: reliable alternative to SSE timing ───────────
  // When Layout.tsx adds a job with autoProcess:true it sets autoProcessPending.
  // MasterPromptTab reads this flag on every mount (tab switch) and triggers
  // Start Processing immediately — no SSE timing issues.
  useEffect(() => {
    const store = useStore.getState();
    if (!store.autoProcessPending) return;
    store.setAutoProcessPending(false);
    const hasPending = store.mpJobs.some(j => j.script && !j.shotsCreated);
    if (hasPending && !isRunning) {
      // Small delay so React finishes rendering the new job in the list first
      setTimeout(() => handleStartProcessing(false), 400);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  });  // ← no dep array: runs on EVERY render (i.e. each time tab is shown)

  // ── Shared authenticated feed: backup trigger for start-processing ─
  useEffect(() => {
    return subscribeNodeGraphEvents(data => {
      if (data.type === 'master-prompt-start-processing') {
        const hasPending = useStore.getState().mpJobs.some(j => j.script && !j.shotsCreated);
        if (hasPending && !isRunning) handleStartProcessing(false);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeCli = CLI_PROVIDERS.find(p => p.id === mpCliProvider)!;

  const updateUgcFlowkitConfig = <Key extends keyof UgcFlowkitLaunchConfig>(key: Key, value: UgcFlowkitLaunchConfig[Key]) => {
    setUgcFlowkitPreview(null);
    setUgcFlowkitSelectedProductIds(null);
    setUgcFlowkitConfig(current => ({ ...current, [key]: value }));
  };

  const handleStartUgcFlowkit = async () => {
    setUgcFlowkitStarting(true);
    setActionError('');
    setUgcFlowkitResult('');
    try {
      const previewMatchesConfig = ugcFlowkitPreview?.key === JSON.stringify(ugcFlowkitConfig);
      if (previewMatchesConfig && ugcFlowkitSelectedProductIds?.length === 0) {
        throw new Error('Select at least one product before starting the batch.');
      }
      const configToLaunch = {
        ...ugcFlowkitConfig,
        ...(previewMatchesConfig && ugcFlowkitSelectedProductIds
          ? { selectedProductIds: ugcFlowkitSelectedProductIds }
          : {}),
        ...(ugcFlowkitConfig.promptOnly ? { autoGenerate: false, autoMerge: false } : {}),
      };
      const result = await startUgcFlowkitPipeline(configToLaunch);
      if (result.isQueued) {
        setUgcFlowkitResult(`Batch ${result.batchId} đã được thêm vào hàng đợi (#${result.queuePosition || 1}) và sẽ tự động bắt đầu sau khi batch hiện tại hoàn tất.`);
        setV2RightTab('batches');
      } else {
        setUgcFlowkitResult(`Batch ${result.batchId} đang chạy với ${result.products.length} product${result.products.length === 1 ? '' : 's'}.`);
        setV2RightTab('prompts');
      }
      setExplicitlySelectedBatchId(result.batchId);
      setUgcFlowkitBatchId(result.batchId);
      if (ugcMasterVersion === 'v1') {
        setUgcFlowkitOpen(false);
        setActiveTab('ugcFlowkit');
      } else {
        try {
          const loaded = await getUgcFlowkitBatch(result.batchId);
          setV2Batch(loaded);
        } catch {
          // fallback
        }
      }
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Unable to start UGC Flowkit pipeline.');
    } finally {
      setUgcFlowkitStarting(false);
    }
  };

  const refreshV2Batch = async () => {
    const idToFetch = ugcFlowkitBatchId;
    setV2BatchLoading(true);
    try {
      if (!idToFetch || getExplicitlySelectedBatchId() !== idToFetch) {
        const current = await getCurrentUgcFlowkitBatch();
        if (current) {
          setExplicitlySelectedBatchId(current.id);
          setV2Batch(current);
          if (current.id !== idToFetch) {
            setUgcFlowkitBatchId(current.id);
          }
          return;
        }
      }
      if (idToFetch) {
        const batchData = await getUgcFlowkitBatch(idToFetch);
        setExplicitlySelectedBatchId(batchData.id);
        setV2Batch(batchData);
      }
    } catch {
      try {
        const current = await getCurrentUgcFlowkitBatch();
        if (current) {
          setExplicitlySelectedBatchId(current.id);
          setV2Batch(current);
          setUgcFlowkitBatchId(current.id);
        }
      } catch {
        // ignore
      }
    } finally {
      setV2BatchLoading(false);
    }
  };

  const [v2ActionLoading, setV2ActionLoading] = useState<string | null>(null);

  const handleResumeV2Batch = async () => {
    if (!v2Batch?.id) return;
    setV2ActionLoading('resume-batch');
    setActionError('');
    try {
      await resumeUgcFlowkitBatch(v2Batch.id);
      await refreshV2Batch();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to resume batch');
    } finally {
      setV2ActionLoading(null);
    }
  };

  const handleStopV2Batch = async () => {
    if (!v2Batch?.id) return;
    setV2ActionLoading('stop-batch');
    setActionError('');
    try {
      await stopUgcFlowkitBatch(v2Batch.id);
      await refreshV2Batch();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to stop batch');
    } finally {
      setV2ActionLoading(null);
    }
  };

  const handleResumeProduct = async (productId: string) => {
    if (!v2Batch?.id) return;
    setV2ActionLoading(`resume-${productId}`);
    setActionError('');
    try {
      await resumeUgcFlowkitProduct(v2Batch.id, productId);
      await refreshV2Batch();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to resume product');
    } finally {
      setV2ActionLoading(null);
    }
  };

  useEffect(() => {
    if (ugcMasterVersion === 'v1') return;
    let timer: any = null;
    let cancelled = false;

    const poll = async () => {
      const idToFetch = useStore.getState().ugcFlowkitBatchId;
      if (!idToFetch || getExplicitlySelectedBatchId() !== idToFetch) {
        try {
          const current = await getCurrentUgcFlowkitBatch();
          if (!cancelled && current) {
            setExplicitlySelectedBatchId(current.id);
            setV2Batch(current);
            if (current.id !== idToFetch) {
              setUgcFlowkitBatchId(current.id);
            }
            const inProgress = !current.runtime?.blocked && current.products.some(p => p.status !== 'merged' && p.status !== 'stopped_safe');
            if (inProgress) {
              timer = setTimeout(poll, 3000);
            }
            return;
          }
        } catch {
          // fall through to idToFetch if available
        }
        if (!idToFetch) return;
      }
      try {
        const batchData = await getUgcFlowkitBatch(idToFetch);
        if (!cancelled) {
          setExplicitlySelectedBatchId(batchData.id);
          setV2Batch(batchData);
          const inProgress = !batchData.runtime?.blocked && batchData.products.some(p => p.status !== 'merged' && p.status !== 'stopped_safe');
          if (inProgress) {
            timer = setTimeout(poll, 3000);
          }
        }
      } catch {
        try {
          const current = await getCurrentUgcFlowkitBatch();
          if (!cancelled && current) {
            setExplicitlySelectedBatchId(current.id);
            setV2Batch(current);
            setUgcFlowkitBatchId(current.id);
          }
        } catch {
          // ignore
        }
      }
    };

    void poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [ugcMasterVersion, ugcFlowkitBatchId, setUgcFlowkitBatchId]);

  const handlePreviewUgcFlowkit = async () => {
    setUgcFlowkitStarting(true);
    setActionError('');
    try {
      const value = await previewUgcFlowkitPipeline(ugcFlowkitConfig);
      setUgcFlowkitPreview({ key: JSON.stringify(ugcFlowkitConfig), value });
      setUgcFlowkitSelectedProductIds(value.products.map(product => product.id));
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Unable to preview UGC Flowkit selection.');
    } finally {
      setUgcFlowkitStarting(false);
    }
  };

  const currentUgcFlowkitPreview = ugcFlowkitPreview?.key === JSON.stringify(ugcFlowkitConfig)
    ? ugcFlowkitPreview.value
    : null;
  const selectedUgcFlowkitProductIds = new Set(
    ugcFlowkitSelectedProductIds ?? currentUgcFlowkitPreview?.products.map(product => product.id) ?? [],
  );
  const selectedUgcFlowkitProducts = currentUgcFlowkitPreview?.products.filter(product => selectedUgcFlowkitProductIds.has(product.id)) ?? [];
  const toggleUgcFlowkitProduct = (productId: string, checked: boolean) => {
    if (!currentUgcFlowkitPreview) return;
    setUgcFlowkitSelectedProductIds(current => {
      const selected = new Set(current ?? currentUgcFlowkitPreview.products.map(product => product.id));
      if (checked) selected.add(productId);
      else selected.delete(productId);
      return currentUgcFlowkitPreview.products.map(product => product.id).filter(id => selected.has(id));
    });
  };

  const handleAddToQueue = async () => {
    if (queuingRef.current || !ideas.trim()) return;
    if (selectedSharedFamilyMode) {
      setActionError('Character UGC uses the shared family roster. Open Start pipeline to select products and run UGC Flowkit; manual portrait upload is not used.');
      return;
    }
    if (isCharacterUgc && portraitUploadsRef.current) {
      setActionError('Portrait upload in progress. Wait for both portraits before queuing.');
      return;
    }
    queuingRef.current = true;
    setIsQueuing(true);
    setActionError('');
    const source = ideas;
    const mode = mpVideoMode;
    const settings = {
      technique, material, category,
      duration: isCharacterUgc ? 32 : mpDuration,
      language: mpLanguage,
      aspect: mpAspect,
      ugcMode: isCharacterUgc ? 'character' as const : 'faceless' as const,
    };
    try {
      if (engineError) throw new Error(engineError);
      const characterUgc = isCharacterUgc ? await createCharacterUgcSnapshot(mpCharacterUgc) : undefined;
      const entries = mode === 'script' ? [source.trim()] : source.split('\n').map(line => line.trim()).filter(Boolean);
      for (const entry of entries) {
        addMpJob({
          ...settings,
          id: crypto.randomUUID(),
          idea: mode === 'script' ? entry.split('\n')[0].slice(0, 120) : entry,
          progress: mode === 'script' ? 100 : 0,
          status: mode === 'script' ? 'done' : 'pending',
          ...(mode === 'script' ? { script: entry } : {}),
          ...(characterUgc ? { characterUgc: {
            version: characterUgc.version,
            characters: characterUgc.characters.map(character => ({ ...character })),
          } } : {}),
        });
      }
      setIdeas(current => current === source ? '' : current);
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Unable to queue this job.');
    } finally {
      queuingRef.current = false;
      setIsQueuing(false);
    }
  };

  const handleBulkImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.txt,.csv';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        const text = ev.target?.result as string;
        setIdeas(prev => prev ? prev + '\n' + text : text);
      };
      reader.readAsText(file);
    };
    input.click();
  };

  const handleStartProcessing = async (retryErrors = true) => {
    // Re-entry guard: if already running, queue the next batch to run after current finishes
    if (processingRef.current) return;
    // Only process jobs that have a script but no shots yet (FIFO accumulation).
    // We check both the shotsCreated flag AND whether shots already exist for
    // that jobId so re-clicks never wipe or duplicate existing work.
    const existingJobIds = new Set(useStore.getState().shots.map(s => s.jobId));
    const withScript = useStore.getState().mpJobs.filter(j => j.script && !j.shotsCreated && !existingJobIds.has(j.id) && (retryErrors || j.status !== 'error'));
    if (!withScript.length) return;
    const attemptedJobIds = new Set(withScript.map(job => job.id));
    processingRef.current = true;
    setIsRunning(true);
    if (retryErrors) setActionError('');
    abortRef.current = false;
    // ⚠ DO NOT call clearShots() here — new shots must APPEND to the existing
    // queue so all UGC videos accumulate in one First-In-First-Served list.

    const allNewShots: ShotItem[] = [];

    for (const { id: jobId } of withScript) {
      if (abortRef.current) break;
      const current = useStore.getState();
      const job = current.mpJobs.find(candidate => candidate.id === jobId);
      if (!job || !job.script || job.shotsCreated || current.shots.some(shot => shot.jobId === jobId)
        || (!retryErrors && job.status === 'error')) continue;
      setProcessingMsg(`Parsing shots for: ${job.idea.slice(0, 50)}…`);
      updateMpJob(job.id, { status: 'running', progress: 10 });
      setProcessingJobId(job.id);

      try {
        if (job.characterUgc?.characters.some(character => !character.imageUrl)) {
          throw new Error('Character UGC: reattach both original portraits to this queued job before processing.');
        }
        const perShotDuration = mpVideoMode === 'script' ? 8 : Math.round(job.duration / 5);
        const res = await api.post('/script/shots', job.characterUgc
          ? { script: job.script, defaultDuration: 8, ugcMode: 'character', characterUgc: job.characterUgc }
          : { script: job.script, defaultDuration: perShotDuration });
        const parsed = res.data.shots as Pick<ShotItem, 'shotNumber' | 'prompt' | 'voiceover' | 'durationSec' | 'characterRoles' | 'characterUgcVersion'>[];

        const newShots: ShotItem[] = parsed.map(s => ({
          id: crypto.randomUUID(),
          jobId: job.id,
          shotNumber: s.shotNumber,
          prompt: s.prompt,
          voiceover: s.voiceover,
          durationSec: s.durationSec,
          ...(s.characterRoles !== undefined ? { characterRoles: [...s.characterRoles] } : {}),
          ...(s.characterUgcVersion !== undefined ? { characterUgcVersion: s.characterUgcVersion } : {}),
          selected: true,
          // Use job-level images if set; else fall back to Auto Mode global images
          imageDataUrls: job.jobImageUrls?.length ? [...job.jobImageUrls]
                       : autoMode ? [...autoProductImageUrls] : [],
          imageFiles:    job.jobImageFiles?.length ? [...job.jobImageFiles]
                       : autoMode ? [...autoProductFileNames] : [],
          status: 'pending' as const,
        }));
        addShots(newShots);
        allNewShots.push(...newShots);
        updateMpJob(job.id, { status: 'done', progress: 100, shotsCreated: true });
        setProcessingMsg(`${parsed.length} shots created`);
      } catch (e: any) {
        updateMpJob(job.id, { status: 'error', progress: 0 });
        const message = typeof e.response?.data?.error === 'string' ? e.response.data.error : e.message || 'Unable to parse this script.';
        setProcessingMsg(`Error: ${message}`);
        setActionError(message);
      }
    }

    processingRef.current = false;
    setIsRunning(false);

    // Auto Mode: enable auto-merge + signal Normal Panel to start generating
    // Only trigger if we actually created new shots this run
    if (autoMode && allNewShots.length > 0) {
      setAutoMerge(true);
      setAutoGeneratePending(true);
      useStore.getState().setActiveTab('normalPanel');
    }

    // If more unprocessed jobs arrived while we were running, process them now
    const remainingJobIds = new Set(useStore.getState().shots.map(s => s.jobId));
    const stillPending = useStore.getState().mpJobs.filter(j => j.script && !j.shotsCreated && j.status !== 'error' && !remainingJobIds.has(j.id) && !attemptedJobIds.has(j.id));
    if (!abortRef.current && stillPending.length > 0) {
      // Re-run to pick up any jobs that arrived during processing
      setTimeout(() => handleStartProcessing(false), 300);
    }
  };

  const handleStopProcessing = () => {
    abortRef.current = true;
    setIsRunning(false);
    setProcessingMsg('Stopped.');
  };

  const handleDeleteSelected = () => {
    clearShots();
    setProcessingJobId(null);
  };

  const handleJobImageAdd = (jobId: string, files: FileList | null) => {
    if (!files?.length) return;
    Promise.all(
      Array.from(files).map(f => new Promise<{ url: string; name: string }>(resolve => {
        const reader = new FileReader();
        reader.onload = e => resolve({ url: e.target?.result as string, name: f.name });
        reader.readAsDataURL(f);
      }))
    ).then(results => {
      const job = mpJobs.find(j => j.id === jobId);
      updateMpJob(jobId, {
        jobImageUrls:  [...(job?.jobImageUrls  || []), ...results.map(r => r.url)],
        jobImageFiles: [...(job?.jobImageFiles || []), ...results.map(r => r.name)],
      });
    });
  };

  const handleJobImageRemove = (jobId: string, idx: number) => {
    const job = mpJobs.find(j => j.id === jobId);
    if (!job) return;
    updateMpJob(jobId, {
      jobImageUrls:  job.jobImageUrls?.filter((_, i) => i !== idx),
      jobImageFiles: job.jobImageFiles?.filter((_, i) => i !== idx),
    });
  };

  const updateDraftCharacter = (role: CharacterUgcRole, patch: Partial<Omit<CharacterUgcCharacter, 'role'>>) => {
    const current = useStore.getState().mpCharacterUgc;
    setMpCharacterUgc({ ...current, characters: current.characters.map(character => character.role === role ? { ...character, ...patch } : character) });
  };

  const handleDraftPortrait = async (role: CharacterUgcRole, file: File | null) => {
    if (!mountedRef.current) return;
    const request = ++portraitRequestsRef.current[role];
    if (!file) {
      updateDraftCharacter(role, { imageUrl: undefined, imageFile: undefined, imageSha256: undefined });
      return;
    }
    portraitUploadsRef.current++;
    setUploadingPortraits(true);
    try {
      const portrait = await validateCharacterPortrait(file);
      if (mountedRef.current && portraitRequestsRef.current[role] === request) updateDraftCharacter(role, portrait);
    } catch (error) {
      if (mountedRef.current && portraitRequestsRef.current[role] === request) throw error;
    } finally {
      portraitUploadsRef.current--;
      if (mountedRef.current) setUploadingPortraits(portraitUploadsRef.current > 0);
    }
  };

  const handleJobPortrait = async (jobId: string, role: CharacterUgcRole, file: File | null) => {
    const original = useStore.getState().mpJobs.find(job => job.id === jobId)?.characterUgc;
    if (!original) throw new Error('Character UGC: this queued job is no longer available.');
    const expectedHash = original.characters.find(character => character.role === role)?.imageSha256;
    const portrait = file ? (await reattachCharacterReference(original, role, file)).characters.find(character => character.role === role) : undefined;
    const current = useStore.getState().mpJobs.find(job => job.id === jobId)?.characterUgc;
    if (!current || current.characters.find(character => character.role === role)?.imageSha256 !== expectedHash) {
      throw new Error('Character UGC: the queued reference changed during upload. Reopen this job and select its original portrait.');
    }
    updateMpJob(jobId, { characterUgc: {
      ...current,
      characters: current.characters.map(character => character.role === role ? {
        ...character,
        imageUrl: portrait?.imageUrl,
        ...(portrait ? { imageFile: portrait.imageFile } : {}),
      } : character),
    } });
  };

  const handleCreateAllScripts = () => { const first = mpJobs.find(j => j.status === 'pending'); if (first) setScriptJob(first); };
  const handleCreateScript = (job: PromptJob) => { setScriptJob(job); };
  const handleJobUpdate = (id: string, status: PromptJob['status'], progress: number, review?: string, script?: string) => {
    updateMpJob(id, { status, progress, ...(review ? { review } : {}), ...(script ? { script } : {}) });
  };

  const activeJob = processingJobId ? mpJobs.find(j => j.id === processingJobId) : null;

  const statusColor: Record<PromptJob['status'], string> = {
    pending: 'text-[#6d5b3f]',
    running: 'text-[#258cc0]',
    done:    'text-[#476b24]',
    error:   'text-[#8c2d1f]',
  };

  const statusBg: Record<PromptJob['status'], string> = {
    pending: 'bg-[#e9d6aa]/50',
    running: 'bg-[#258cc0]/15',
    done:    'bg-[#76a441]/15',
    error:   'bg-[#dc553c]/15',
  };

  return (
    <div className="master-prompt-tab h-full flex flex-col overflow-hidden bg-[#fdf6e9]">
      {/* ── CLI Provider Tabs & Version Switcher ── */}
      <div className="master-prompt-toolbar studio-toolbar flex items-center gap-2 border-b px-4 py-2 shrink-0 bg-[#fff8ec]">
        {/* Version Switcher */}
        <div className="flex items-center rounded-lg border border-[#d6bd7c] bg-[#e9d6aa]/40 p-0.5">
          <button
            type="button"
            onClick={() => setUgcMasterVersion('v1')}
            className={`rounded-md px-3 py-1 text-xs font-bold transition-all ${
              ugcMasterVersion === 'v1'
                ? 'bg-[#865916] text-[#fff8ec] shadow-sm'
                : 'text-[#6d5b3f] hover:text-[#2f2414]'
            }`}
          >
            v1 (Classic)
          </button>
          <button
            type="button"
            onClick={() => setUgcMasterVersion('v2')}
            className={`rounded-md px-3 py-1 text-xs font-bold transition-all ${
              ugcMasterVersion === 'v2'
                ? 'bg-[#865916] text-[#fff8ec] shadow-sm'
                : 'text-[#6d5b3f] hover:text-[#2f2414]'
            }`}
          >
            v2 (Pipeline)
          </button>
          <button
            type="button"
            onClick={() => setUgcMasterVersion('v3')}
            className={`rounded-md px-3 py-1 text-xs font-bold transition-all ${
              ugcMasterVersion === 'v3'
                ? 'bg-[#476b24] text-[#fff8ec] shadow-sm'
                : 'text-[#6d5b3f] hover:text-[#2f2414]'
            }`}
          >
            v3 (Affiliate Harness)
          </button>
        </div>

        <div className="h-4 w-[1px] bg-[#d6bd7c]/60 mx-1" />

        {ugcMasterVersion === 'v1' ? (
          <>
            {CLI_PROVIDERS.map(cli => (
              <button
                key={cli.id}
                onClick={() => setMpCliProvider(cli.id)}
                className={`flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-bold transition-colors ${
                  mpCliProvider === cli.id
                    ? 'bg-[#2f2414] text-[#fff8ec]'
                    : 'text-[#6d5b3f] hover:bg-[#e9d6aa]/60 hover:text-[#2f2414]'
                }`}
              >
                <cli.icon className="h-4 w-4" style={{ color: mpCliProvider === cli.id ? cli.color : undefined }} />
                {cli.label}
              </button>
            ))}
            <div className="flex-1" />
            {/* Processing controls */}
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => { setUgcFlowkitOpen(true); setUgcFlowkitResult(''); setUgcFlowkitPreview(null); setActionError(''); }}
                className="flex items-center gap-1.5 rounded-md bg-[#865916] px-3 py-1.5 text-xs font-bold text-[#fff8ec] hover:bg-[#6f4810] transition-colors"
              >
                <PlayCircle className="w-3.5 h-3.5" />Start pipeline
              </button>
              {processingMsg && <span className="text-[10px] text-[#6d5b3f] max-w-[200px] truncate">{processingMsg}</span>}
              {isRunning ? (
                <button onClick={handleStopProcessing} className="flex items-center gap-1.5 rounded-md border border-[#dc553c]/50 bg-[#dc553c]/10 px-3 py-1.5 text-xs font-bold text-[#8c2d1f] hover:bg-[#dc553c]/20 transition-colors">
                  <StopCircle className="w-3.5 h-3.5" />Stop
                </button>
              ) : (
                <button
                  onClick={() => handleStartProcessing()}
                  disabled={isRunning || !mpJobs.some(j => j.script)}
                  className="flex items-center gap-1.5 rounded-md bg-[#476b24] px-3 py-1.5 text-xs font-bold text-[#fff8ec] hover:bg-[#395719] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <Scissors className="w-3.5 h-3.5" />Start Processing
                </button>
              )}
              {shots.length > 0 && (
                <button onClick={handleDeleteSelected} className="flex items-center gap-1 rounded-md border border-[#d6bd7c] px-2.5 py-1.5 text-xs font-bold text-[#6d5b3f] hover:bg-[#e9d6aa]/60 transition-colors">
                  <Trash className="w-3.5 h-3.5" />Clear Shots
                </button>
              )}
            </div>
            <span className="flex items-center gap-1.5 rounded-md border border-[#d6bd7c]/60 px-2 py-1 text-xs font-bold text-[#865916]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#76a441] animate-pulse" />
              {activeCli.label} ready
            </span>
          </>
        ) : (
          <>
            <span className="text-xs font-black text-[#2f2414]">UGC Master</span>
            <div className="flex-1" />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setV2RightTab(t => t === 'batches' ? 'prompts' : 'batches')}
                className={`flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-bold transition-colors ${
                  v2RightTab === 'batches'
                    ? 'border-[#865916] bg-[#865916] text-[#fff8ec]'
                    : 'border-[#d6bd7c] bg-[#fff8ec] text-[#865916] hover:bg-[#e9d6aa]/50'
                }`}
              >
                <History className="w-3.5 h-3.5" />
                {v2RightTab === 'batches' ? 'Xem Prompts & Kịch bản' : 'Batches & Hàng đợi'}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (v2Batch?.id && ugcFlowkitBatchId !== v2Batch.id) {
                    setUgcFlowkitBatchId(v2Batch.id);
                  }
                  setActiveTab('ugcFlowkit');
                }}
                className="flex items-center gap-1.5 rounded-md border border-[#d6bd7c] bg-[#fff8ec] px-3 py-1.5 text-xs font-bold text-[#865916] hover:bg-[#e9d6aa]/50 transition-colors"
              >
                <ExternalLink className="w-3.5 h-3.5" />Open Pipeline Workspace
              </button>
              <span className="flex items-center gap-1.5 rounded-md border border-[#76a441]/40 bg-[#76a441]/10 px-2.5 py-1 text-xs font-bold text-[#476b24]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#76a441] animate-pulse" />
                Pipeline v2 Active
              </span>
            </div>
          </>
        )}
      </div>

      {(engineError || actionError) && (
        <div role="alert" className="shrink-0 border-b border-[#dc553c]/30 px-4 py-2 text-xs text-[#8c2d1f] whitespace-pre-wrap break-words">
          {engineError || actionError}
        </div>
      )}
      {ugcFlowkitResult && (
        <div role="status" className="shrink-0 border-b border-[#76a441]/30 bg-[#76a441]/10 px-4 py-2 text-xs font-bold text-[#476b24]">
          {ugcFlowkitResult}
        </div>
      )}

      {ugcFlowkitOpen && (
        <div role="dialog" aria-modal="true" aria-labelledby="ugc-flowkit-title" className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-[#2f2414]/35 p-5">
          <form onSubmit={event => { event.preventDefault(); void handleStartUgcFlowkit(); }} className="my-4 w-full max-w-3xl rounded-xl border border-[#d6bd7c] bg-[#fff8ec] shadow-2xl">
            <div className="flex items-start justify-between border-b border-[#d6bd7c]/60 px-5 py-4">
              <div>
                <h2 id="ugc-flowkit-title" className="text-sm font-black text-[#2f2414]">Start UGC Flowkit pipeline</h2>
                <p className="mt-1 text-xs text-[#6d5b3f]">Dedicated product runtime — not Film and not legacy 32s Character UGC.</p>
              </div>
              <button type="button" aria-label="Close UGC Flowkit configuration" onClick={() => setUgcFlowkitOpen(false)} className="rounded p-1 text-[#6d5b3f] hover:bg-[#e9d6aa]/60"><X className="h-4 w-4" /></button>
            </div>
            <div className="grid gap-4 p-5 sm:grid-cols-2">
              <label className="text-xs font-bold text-[#6d5b3f]">Win products
                <div className="mt-1 flex gap-2">
                  <select value={ugcFlowkitConfig.winProducts === 'all' ? 'all' : 'count'} onChange={event => updateUgcFlowkitConfig('winProducts', event.target.value === 'all' ? 'all' : 1)} className="rounded border border-[#d6bd7c] bg-white px-2 py-1.5 text-xs text-[#2f2414]">
                    <option value="all">All</option><option value="count">Count</option>
                  </select>
                  {ugcFlowkitConfig.winProducts !== 'all' && <input aria-label="Win product count" required type="number" min="0" max="10000" value={ugcFlowkitConfig.winProducts} onChange={event => updateUgcFlowkitConfig('winProducts', Number(event.target.value))} className="w-20 rounded border border-[#d6bd7c] bg-white px-2 py-1.5 text-xs" />}
                </div>
              </label>
              <label className="text-xs font-bold text-[#6d5b3f]">Duration (seconds)
                <input required type="number" min="8" max="120" value={ugcFlowkitConfig.durationSec} onChange={event => updateUgcFlowkitConfig('durationSec', Number(event.target.value))} className="mt-1 block w-full rounded border border-[#d6bd7c] bg-white px-2 py-1.5 text-xs text-[#2f2414]" />
              </label>
              <label className="text-xs font-bold text-[#6d5b3f]">Best products
                <input required type="number" min="0" max="10000" value={ugcFlowkitConfig.bestProducts} onChange={event => updateUgcFlowkitConfig('bestProducts', Number(event.target.value))} className="mt-1 block w-full rounded border border-[#d6bd7c] bg-white px-2 py-1.5 text-xs text-[#2f2414]" />
              </label>
              <label className="text-xs font-bold text-[#6d5b3f]">Good products
                <input required type="number" min="0" max="10000" value={ugcFlowkitConfig.goodProducts} onChange={event => updateUgcFlowkitConfig('goodProducts', Number(event.target.value))} className="mt-1 block w-full rounded border border-[#d6bd7c] bg-white px-2 py-1.5 text-xs text-[#2f2414]" />
              </label>
              <label className="text-xs font-bold text-[#6d5b3f]">Mode
                <select value={ugcFlowkitConfig.mode} onChange={event => updateUgcFlowkitConfig('mode', event.target.value as UgcFlowkitLaunchConfig['mode'])} className="mt-1 block w-full rounded border border-[#d6bd7c] bg-white px-2 py-1.5 text-xs text-[#2f2414]"><option value="faceless">Faceless</option><option value="character">Character family</option></select>
              </label>
              <label className="text-xs font-bold text-[#6d5b3f]">Location
                <select value={ugcFlowkitConfig.locationStrategy} onChange={event => updateUgcFlowkitConfig('locationStrategy', event.target.value as UgcFlowkitLaunchConfig['locationStrategy'])} className="mt-1 block w-full rounded border border-[#d6bd7c] bg-white px-2 py-1.5 text-xs text-[#2f2414]"><option value="generated-per-video">Generated per video</option><option value="folder">Use location folder</option></select>
              </label>
              <label className="sm:col-span-2 text-xs font-bold text-[#6d5b3f]">Product images folder
                <input required value={ugcFlowkitConfig.productImageFolder} onChange={event => updateUgcFlowkitConfig('productImageFolder', event.target.value)} className="mt-1 block w-full rounded border border-[#d6bd7c] bg-white px-2 py-1.5 text-xs text-[#2f2414]" />
              </label>
              <label className="sm:col-span-2 text-xs font-bold text-[#6d5b3f]">Family folder (shared voices in both modes; portraits only in Character)
                <input required value={ugcFlowkitConfig.familyCharacterFolder} onChange={event => updateUgcFlowkitConfig('familyCharacterFolder', event.target.value)} className="mt-1 block w-full rounded border border-[#d6bd7c] bg-white px-2 py-1.5 text-xs text-[#2f2414]" />
              </label>
              {ugcFlowkitConfig.locationStrategy === 'folder' && <label className="sm:col-span-2 text-xs font-bold text-[#6d5b3f]">Location folder
                <input required value={ugcFlowkitConfig.locationFolder} onChange={event => updateUgcFlowkitConfig('locationFolder', event.target.value)} className="mt-1 block w-full rounded border border-[#d6bd7c] bg-white px-2 py-1.5 text-xs text-[#2f2414]" />
              </label>}
              <div className="sm:col-span-2 flex flex-col gap-2 rounded border border-[#d6bd7c]/60 bg-[#fdf6e9] p-3 text-xs text-[#2f2414]">
                <label className="flex items-center gap-2 font-bold text-[#176184] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={Boolean(ugcFlowkitConfig.promptOnly)}
                    onChange={event => {
                      const checked = event.target.checked;
                      updateUgcFlowkitConfig('promptOnly', checked);
                      if (checked) {
                        updateUgcFlowkitConfig('autoGenerate', false);
                        updateUgcFlowkitConfig('autoMerge', false);
                      }
                    }}
                    className="accent-[#258cc0]"
                  />
                  <Sparkles className="w-3.5 h-3.5 text-[#258cc0]" />
                  Prompt Generate Only (Google Flow manual copy-paste)
                </label>
                <div className="flex flex-wrap gap-5 pt-1 border-t border-[#d6bd7c]/40">
                  <label className="flex items-center gap-2 font-bold">
                    <input
                      type="checkbox"
                      checked={ugcFlowkitConfig.autoGenerate && !ugcFlowkitConfig.promptOnly}
                      disabled={Boolean(ugcFlowkitConfig.promptOnly)}
                      onChange={event => updateUgcFlowkitConfig('autoGenerate', event.target.checked)}
                    />
                    Auto generate
                  </label>
                  <label className="flex items-center gap-2 font-bold">
                    <input
                      type="checkbox"
                      checked={ugcFlowkitConfig.autoMerge && !ugcFlowkitConfig.promptOnly}
                      disabled={!ugcFlowkitConfig.autoGenerate || Boolean(ugcFlowkitConfig.promptOnly)}
                      onChange={event => updateUgcFlowkitConfig('autoMerge', event.target.checked)}
                    />
                    Auto merge
                  </label>
                  <span className="text-[#6d5b3f]">Planner: Codex; Gemini only when Codex quota/credit is unavailable.</span>
                </div>
              </div>
              <div className="sm:col-span-2 rounded border border-[#865916]/35 bg-[#865916]/5 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-xs font-black text-[#2f2414]">Preview product selection before Start</p>
                    <p className="mt-0.5 text-[11px] text-[#6d5b3f]">Checks folders and shows exactly what this batch will queue. No job is created.</p>
                  </div>
                  <button type="button" disabled={ugcFlowkitStarting} onClick={() => void handlePreviewUgcFlowkit()} className="rounded border border-[#865916] px-3 py-1.5 text-xs font-bold text-[#865916] hover:bg-[#865916]/10 disabled:opacity-50">Preview selection</button>
                </div>
                {currentUgcFlowkitPreview && (
                  <div className="mt-3 rounded bg-white p-3 text-xs text-[#2f2414]">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-black">Selected: {selectedUgcFlowkitProducts.length} / {currentUgcFlowkitPreview.counts.total} products</p>
                      <div className="flex gap-2 text-[11px] font-bold">
                        <button type="button" onClick={() => setUgcFlowkitSelectedProductIds(currentUgcFlowkitPreview.products.map(product => product.id))} className="text-[#476b24] hover:underline">Select all</button>
                        <button type="button" onClick={() => setUgcFlowkitSelectedProductIds([])} className="text-[#8c2d1f] hover:underline">Clear</button>
                      </div>
                    </div>
                    <div className="mt-2 max-h-40 space-y-1 overflow-y-auto">
                      {currentUgcFlowkitPreview.products.map(product => (
                        <label key={product.id} className="flex items-center gap-2 rounded px-2 py-1 hover:bg-[#e9d6aa]/30 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedUgcFlowkitProductIds.has(product.id)}
                            onChange={event => toggleUgcFlowkitProduct(product.id, event.target.checked)}
                            className="accent-[#476b24]"
                          />
                          <span className="min-w-0 flex-1 truncate">{product.name}</span>
                          <span className="text-[10px] font-bold uppercase text-[#865916]">{product.id.split(':', 1)[0]}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            {actionError && (
              <div role="alert" className="mx-5 mb-2 rounded-lg border border-[#dc553c]/40 bg-[#dc553c]/10 p-3 text-xs font-bold text-[#8c2d1f]">
                {actionError}
              </div>
            )}
            <div className="flex justify-end gap-2 border-t border-[#d6bd7c]/60 px-5 py-4">
              <button type="button" onClick={() => setUgcFlowkitOpen(false)} className="rounded border border-[#d6bd7c] px-3 py-1.5 text-xs font-bold text-[#6d5b3f] hover:bg-[#e9d6aa]/50">Cancel</button>
              <button type="submit" disabled={ugcFlowkitStarting} className="rounded bg-[#476b24] px-4 py-2 text-xs font-bold text-[#fff8ec] hover:bg-[#395719] transition-colors disabled:opacity-50 flex items-center gap-1.5">{ugcFlowkitStarting ? <><RefreshCw className="h-3.5 w-3.5 animate-spin" /><span>Validating & Starting…</span></> : 'Confirm & Start pipeline'}</button>
            </div>
          </form>
        </div>
      )}

      {/* ── Split: config+queue LEFT  |  shot queue RIGHT (v1) OR UGC Pipeline (v2) ── */}
      {ugcMasterVersion === 'v1' ? (
        <div className="master-prompt-body flex-1 flex overflow-hidden">
        <div className="master-prompt-primary flex-1 overflow-y-auto min-w-0">
          {/* ── Step 1: Configuration ── */}
          <div className="border-b border-[#d6bd7c]/40 px-4 py-3">
            <div className="flex items-center gap-2 mb-3">
              <Settings2 className="w-4 h-4 text-[#865916]" />
              <span className="text-xs font-black tracking-[0.12em] uppercase text-[#2f2414]">
                Step 1 — Video Configuration
              </span>
          </div>

          {/* Row 1: Aspect / Quality / Duration / Folder */}
          <div className="flex flex-wrap gap-3 mb-3">
            {/* Aspect */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-[#6d5b3f] w-14">Aspect</span>
              <div className="flex gap-1">
                {ASPECTS.map(a => (
                  <button
                    key={a}
                    onClick={() => setMpAspect(a)}
                    className={`rounded px-2 py-0.5 text-xs font-bold border transition-colors ${
                      mpAspect === a
                        ? 'bg-[#865916] text-[#fff8ec] border-[#865916]'
                        : 'border-[#d6bd7c] text-[#6d5b3f] hover:bg-[#e9d6aa]/60'
                    }`}
                  >{a}</button>
                ))}
              </div>
            </div>

            {/* Quality */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-[#6d5b3f] w-14">Quality</span>
              <div className="flex gap-1">
                {QUALITIES.map(q => (
                  <button
                    key={q}
                    onClick={() => setMpQuality(q)}
                    className={`rounded px-2 py-0.5 text-xs font-bold border transition-colors ${
                      mpQuality === q
                        ? 'bg-[#865916] text-[#fff8ec] border-[#865916]'
                        : 'border-[#d6bd7c] text-[#6d5b3f] hover:bg-[#e9d6aa]/60'
                    }`}
                  >{q}</button>
                ))}
              </div>
            </div>

            {/* Duration */}
            <div className="flex items-center gap-2">
              <label htmlFor="mp-duration" className="text-xs font-bold text-[#6d5b3f]">Duration</label>
              <input
                id="mp-duration"
                aria-label="Duration"
                type="number"
                min={isCharacterUgc ? 32 : 15} max={isCharacterUgc ? 32 : 600} step={isCharacterUgc ? 1 : 15}
                value={isCharacterUgc ? 32 : mpDuration}
                readOnly={isCharacterUgc}
                onChange={e => { if (!isCharacterUgc) setMpDuration(Number(e.target.value)); }}
                className="w-20 rounded border border-[#d6bd7c] bg-[#fff8ec] px-2 py-0.5 text-xs font-bold text-[#2f2414] focus:outline-none focus:ring-1 focus:ring-[#865916]"
              />
              <span className="text-xs text-[#6d5b3f]">sec</span>
            </div>

            {/* Language */}
            <div className="flex items-center gap-2">
              <Globe className="w-3 h-3 text-[#865916]" />
              <select
                value={mpLanguage}
                onChange={e => setMpLanguage(e.target.value)}
                className="rounded border border-[#d6bd7c] bg-[#fff8ec] px-2 py-0.5 text-xs font-bold text-[#2f2414] focus:outline-none focus:ring-1 focus:ring-[#865916]"
              >
                {LANGUAGES.map(l => (
                  <option key={l} value={l}>{LANG_LABELS[l]}</option>
                ))}
              </select>
            </div>

            {/* Folder */}
            <div className="flex items-center gap-2 flex-1 min-w-[140px]">
              <FolderOpen className="w-3 h-3 text-[#865916]" />
              <input
                type="text"
                value={mpFolder}
                onChange={e => setMpFolder(e.target.value)}
                placeholder="Output folder…"
                className="flex-1 rounded border border-[#d6bd7c] bg-[#fff8ec] px-2 py-0.5 text-xs font-bold text-[#2f2414] placeholder:font-normal placeholder:text-[#a89470] focus:outline-none focus:ring-1 focus:ring-[#865916]"
              />
            </div>
          </div>

          {/* Row 2: Technique / Material / Category */}
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-[#6d5b3f] w-20">Technique</span>
              <select
                aria-label="Technique"
                value={technique}
                onChange={e => setTechnique(e.target.value)}
                className="rounded border border-[#d6bd7c] bg-[#fff8ec] px-2 py-0.5 text-xs font-bold text-[#2f2414] focus:outline-none focus:ring-1 focus:ring-[#865916]"
              >
                {TECHNIQUES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-[#6d5b3f] w-16">Material</span>
              <select
                value={material}
                onChange={e => setMaterial(e.target.value)}
                className="rounded border border-[#d6bd7c] bg-[#fff8ec] px-2 py-0.5 text-xs font-bold text-[#2f2414] focus:outline-none focus:ring-1 focus:ring-[#865916]"
              >
                {MATERIALS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-[#6d5b3f] w-16">Category</span>
              <select
                value={category}
                onChange={e => setCategory(e.target.value)}
                className="rounded border border-[#d6bd7c] bg-[#fff8ec] px-2 py-0.5 text-xs font-bold text-[#2f2414] focus:outline-none focus:ring-1 focus:ring-[#865916]"
              >
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* ── UGC Mode (shown when technique = UGC) ── */}
        {technique === 'UGC' && (
          <div className="border-b border-[#d6bd7c]/40 px-4 py-3">
            <div className="flex items-center gap-2 mb-2">
              <UserCircle2 className="w-4 h-4 text-[#865916]" />
              <span className="text-xs font-black tracking-[0.10em] uppercase text-[#2f2414]">UGC Mode</span>
            </div>
            <div role="group" aria-label="UGC mode" className="flex gap-3 flex-wrap">
              {([
                { id: 'faceless' as const, label: 'Faceless', icon: EyeOff },
                { id: 'character' as const, label: 'Character', icon: UserCircle2 },
              ]).map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  aria-pressed={mpUgcMode === id}
                  onClick={() => setMpUgcMode(id)}
                  className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-bold transition-all ${
                    mpUgcMode === id
                      ? 'border-[#865916] bg-[#865916]/10 text-[#2f2414] shadow-sm'
                      : 'border-[#d6bd7c] text-[#6d5b3f] hover:border-[#865916]/50 hover:bg-[#fdf6e9]'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span>{label}</span>
                </button>
              ))}
            </div>

            {/* Shared UGC Flowkit family roster: no per-job portrait uploads. */}
            {mpUgcMode === 'character' && (
              <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded border border-[#865916]/35 bg-[#865916]/5 p-3">
                <div>
                  <p className="text-xs font-black text-[#2f2414]">Shared Family Roster</p>
                  <p className="mt-1 text-[11px] text-[#6d5b3f]">Uses the same wife, husband and baby references from <code>data/Characters/family</code> for every product video. No portrait upload, appearance, or voice form.</p>
                </div>
                <button type="button" onClick={() => { setUgcFlowkitConfig(current => ({ ...current, mode: 'character' })); setUgcFlowkitOpen(true); setUgcFlowkitPreview(null); setActionError(''); }} className="rounded border border-[#865916] px-3 py-1.5 text-xs font-bold text-[#865916] hover:bg-[#865916]/10">Configure shared roster</button>
              </div>
            )}
          </div>
        )}

        {/* ── Step 1b: Mode + Library ── */}
        <div className="border-b border-[#d6bd7c]/40 px-4 py-3">
          <div className="flex flex-wrap items-center gap-6">
            {/* Mode */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-[#6d5b3f]">Mode:</span>
              {(['idea', 'script'] as const).map(m => (
                <label key={m} className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="videoMode"
                    checked={mpVideoMode === m}
                    onChange={() => setMpVideoMode(m)}
                    className="accent-[#865916]"
                  />
                  <span className={`text-xs font-bold ${mpVideoMode === m ? 'text-[#2f2414]' : 'text-[#6d5b3f]'}`}>
                    {m === 'idea' ? (
                      <span className="flex items-center gap-1"><Lightbulb className="w-3 h-3" /> Idea</span>
                    ) : (
                      <span className="flex items-center gap-1"><FileText className="w-3 h-3" /> Script</span>
                    )}
                  </span>
                </label>
              ))}
            </div>

            {/* Library */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-[#6d5b3f]">Library:</span>
              {([
                { id: 'ai',      label: 'AI Generate All', icon: Bot },
                { id: 'hybrid',  label: 'Hybrid',          icon: Layers },
                { id: 'library', label: 'Library Only',    icon: FolderOpen },
              ] as const).map(({ id, label, icon: Icon }) => (
                <label key={id} className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="libraryMode"
                    checked={mpLibraryMode === id}
                    onChange={() => setMpLibraryMode(id)}
                    className="accent-[#865916]"
                  />
                  <span className={`flex items-center gap-1 text-xs font-bold ${mpLibraryMode === id ? 'text-[#2f2414]' : 'text-[#6d5b3f]'}`}>
                    <Icon className="w-3 h-3" />{label}
                  </span>
                </label>
              ))}
            </div>

            {/* Engine */}
            <div className="flex items-center gap-2" title={engineLocked ? "Can't switch engines while shots are generating" : undefined}>
              <span className="text-xs font-bold text-[#6d5b3f]">Engine:</span>
              {(['flowboard', 'flowkit'] as const).map(e => (
                <label key={e} className={`flex items-center gap-1.5 ${engineLocked ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                  <input
                    type="radio"
                    name="engine"
                    checked={mpEngine === e}
                    disabled={engineLocked}
                    onChange={() => setMpEngine(e)}
                    className="accent-[#865916]"
                  />
                  <span className={`text-xs font-bold ${mpEngine === e ? 'text-[#2f2414]' : 'text-[#6d5b3f]'}`}>
                    {e === 'flowboard' ? 'Flowboard' : 'Flowkit'}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* ── Auto Mode ── */}
        <div className="border-b border-[#d6bd7c]/40 px-4 py-3">
          <div className="flex items-center gap-4 flex-wrap">
            <label className="flex items-center gap-2 cursor-pointer">
              <div
                onClick={() => setAutoMode(!autoMode)}
                className={`relative w-10 h-5 rounded-full transition-colors ${autoMode ? 'bg-[#865916]' : 'bg-[#d6bd7c]'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${autoMode ? 'translate-x-5' : ''}`} />
              </div>
              <span className="text-xs font-black text-[#2f2414] flex items-center gap-1.5">
                <Zap className="w-3.5 h-3.5 text-[#865916]" />Auto Mode
              </span>
              <span className="text-[10px] text-[#a89470]">
                — upload images once, apply to all shots, auto-generate + auto-merge
              </span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <div
                onClick={() => setAutoMerge(!autoMerge)}
                className={`relative w-10 h-5 rounded-full transition-colors ${autoMerge ? 'bg-[#865916]' : 'bg-[#d6bd7c]'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${autoMerge ? 'translate-x-5' : ''}`} />
              </div>
              <span className="text-xs font-black text-[#2f2414] flex items-center gap-1.5">
                <Merge className="w-3.5 h-3.5 text-[#865916]" />Auto-Merge
              </span>
              <span className="text-[10px] text-[#a89470]">
                — merge all shots into one video automatically once generation finishes
              </span>
            </label>
          </div>

          {/* Product image upload (shown when Auto Mode is on) */}
          {autoMode && (
            <div className="mt-3">
              <p className="text-[10px] font-bold text-[#6d5b3f] mb-2 flex items-center gap-1.5">
                <ImageIcon className="w-3.5 h-3.5" />
                Product images <span className="text-[#a89470] font-normal">(same images will be applied to every shot)</span>
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                {autoProductImageUrls.map((url: string, i: number) => (
                  <div key={i} className="relative group">
                    <img src={url} alt="" className="w-16 h-16 rounded-lg object-cover border border-[#d6bd7c]" />
                    <button
                      onClick={() => {
                        const newUrls = autoProductImageUrls.filter((_: string, idx: number) => idx !== i);
                        const newNames = autoProductFileNames.filter((_: string, idx: number) => idx !== i);
                        setAutoProductImages(newUrls, newNames);
                      }}
                      className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-[#dc553c] text-white hidden group-hover:flex items-center justify-center"
                    >
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </div>
                ))}
                <label className="flex flex-col items-center justify-center w-16 h-16 rounded-lg border-2 border-dashed border-[#d6bd7c] hover:border-[#865916] hover:bg-[#fdf6e9] transition-colors cursor-pointer text-[#a89470] hover:text-[#865916]">
                  <ImageIcon className="w-5 h-5" />
                  <span className="text-[9px] font-bold mt-0.5">+ Add</span>
                  <input type="file" accept="image/*" multiple className="hidden"
                    onChange={e => {
                      const files = Array.from(e.target.files || []);
                      if (!files.length) return;
                      // Read all files in parallel, then append all at once
                      Promise.all(
                        files.map(f => new Promise<{ url: string; name: string }>(resolve => {
                          const reader = new FileReader();
                          reader.onload = ev => resolve({ url: ev.target?.result as string, name: f.name });
                          reader.readAsDataURL(f);
                        }))
                      ).then(results => {
                        setAutoProductImages(
                          [...autoProductImageUrls, ...results.map(r => r.url)],
                          [...autoProductFileNames, ...results.map(r => r.name)]
                        );
                      });
                      // Reset so the same files can be re-selected if needed
                      e.target.value = '';
                    }}
                  />
                </label>
                {autoProductImageUrls.length > 0 && (
                  <button
                    onClick={() => setAutoProductImages([], [])}
                    className="text-[10px] text-[#dc553c] hover:underline ml-1"
                  >Clear all</button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ── Ideas / Script Textarea ── */}
        <div className="px-4 py-3">
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="w-4 h-4 text-[#865916]" />
            <span className="text-xs font-black tracking-[0.1em] uppercase text-[#2f2414]">
              {mpVideoMode === 'script' ? 'Paste Script' : 'Enter Ideas'}
            </span>
            <span className="text-xs text-[#a89470]">
              {mpVideoMode === 'script'
                ? '(paste complete script — shots auto-detected on Start Processing)'
                : '(one idea per line = one video)'}
            </span>
          </div>
          <textarea
            ref={textareaRef}
            aria-label={mpVideoMode === 'script' ? 'Script' : 'Ideas'}
            value={ideas}
            onChange={e => setIdeas(e.target.value)}
            rows={8}
            placeholder={mpVideoMode === 'script'
              ? isCharacterUgc ? 'Couple UGC JSON script' : 'Paste your full script here (Shot 1:, Shot 2:, Scene 1:, etc.)\n\nExample:\nShot 1: Close-up of the product on a wooden table. Camera slowly zooms in.\nVoiceover: "Discover the taste of..."\n\nShot 2: ...'
              : EXAMPLE_IDEAS.map((e, i) => `${i + 1}. ${e}`).join('\n')}
            className="w-full rounded-lg border border-[#d6bd7c] bg-[#fff8ec] px-3 py-2.5 text-sm text-[#2f2414] placeholder:text-[#b8a07a] focus:outline-none focus:ring-2 focus:ring-[#865916]/40 resize-none leading-relaxed"
          />

          {/* Action row */}
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={handleAddToQueue}
              disabled={!ideas.trim() || isQueuing || (isCharacterUgc && uploadingPortraits) || !!engineError}
              aria-busy={isQueuing}
              className="flex items-center gap-1.5 rounded-md bg-[#476b24] px-4 py-2 text-xs font-black tracking-[0.08em] uppercase text-[#fff8ec] hover:bg-[#395719] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Add to Queue
            </button>
            <button
              onClick={handleBulkImport}
              className="flex items-center gap-1.5 rounded-md border border-[#d6bd7c] px-3 py-2 text-xs font-bold text-[#6d5b3f] hover:bg-[#e9d6aa]/60 transition-colors"
            >
              <Upload className="w-3.5 h-3.5" />
              Bulk Import
            </button>
            <div className="flex-1" />
            {/* Step 2 only shown in Idea mode — Script mode pre-populates job.script */}
            {mpVideoMode !== 'script' && (
              <button
                onClick={handleCreateAllScripts}
                disabled={!mpJobs.some(j => j.status === 'pending')}
                className="flex items-center gap-1.5 rounded-md bg-[#865916] px-4 py-2 text-xs font-black tracking-[0.08em] uppercase text-[#fff8ec] hover:bg-[#6d4712] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ScrollText className="w-3.5 h-3.5" />
                Step 2: Create Script
              </button>
            )}
          </div>
        </div>

        {/* ── Batch Queue ── */}
        <div className="px-4 pb-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-black tracking-[0.1em] uppercase text-[#2f2414]">
              Batch Queue
            </span>
            <span className="rounded-full bg-[#865916] px-2 py-0.5 text-[10px] font-black text-[#fff8ec]">
              {mpJobs.length}
            </span>
            <div className="flex-1" />
            {mpJobs.some(j => j.status === 'pending') && (
              <button
                onClick={() => handleStartProcessing()}
                disabled={isRunning}
                className="flex items-center gap-1.5 rounded-md bg-[#258cc0] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#1a6a9e] disabled:opacity-50 transition-colors"
              >
                {isRunning ? <StopCircle className="w-3.5 h-3.5" /> : <PlayCircle className="w-3.5 h-3.5" />}
                {isRunning ? 'Running…' : 'Start Processing'}
              </button>
            )}
            {mpJobs.length > 0 && (
              <button
                onClick={clearMpJobs}
                className="flex items-center gap-1.5 rounded-md border border-[#dc553c]/50 px-2 py-1.5 text-xs font-bold text-[#8c2d1f] hover:bg-[#dc553c]/10 transition-colors"
              >
                <Trash className="w-3.5 h-3.5" />
                Clear All
              </button>
            )}
          </div>

          {mpJobs.length === 0 ? (
            <div className="rounded-lg border border-dashed border-[#d6bd7c] bg-[#fff8ec]/50 py-8 text-center">
              <p className="text-sm text-[#a89470] font-medium">No jobs yet — add ideas above</p>
            </div>
          ) : (
            <div className="rounded-lg border border-[#d6bd7c] overflow-x-auto">
              <table className="min-w-[980px] w-full text-xs">
                <thead>
                  <tr className="bg-[#f4e8c9] border-b border-[#d6bd7c]">
                    {['Idea', 'Technique', 'Material', 'Duration', 'Language', 'Aspect', 'Progress', 'Category', 'Images', 'Script', ''].map(h => (
                      <th key={h} className="px-3 py-2 text-left font-black tracking-[0.08em] uppercase text-[#6d5b3f]">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {mpJobs.map((job, i) => (
                    <tr
                      key={job.id}
                      className={`border-b border-[#e9d6aa] last:border-0 ${i % 2 === 0 ? 'bg-[#fff8ec]' : 'bg-[#fdf6e9]'}`}
                    >
                      <td className="px-3 py-2 min-w-[200px] max-w-[280px] align-top text-[#2f2414]">
                        <div className="truncate font-medium" title={job.idea}>{job.idea}</div>
                        {job.characterUgc && (
                          <>
                            {job.characterUgc.characters.some(character => !character.imageUrl) && (
                              <p role="status" className="mt-1 break-words text-[10px] text-[#8c2d1f]">Original portraits missing: {job.characterUgc.characters.filter(character => !character.imageUrl).map(character => character.role).join(', ')}</p>
                            )}
                            <details className="mt-2">
                              <summary className="cursor-pointer text-[11px] font-bold text-[#865916]">Couple profiles</summary>
                              <div className="mt-3">
                                <CharacterUgcProfiles config={job.characterUgc} readOnlyProfile
                                  onPortraitChange={(role, file) => handleJobPortrait(job.id, role, file)} />
                              </div>
                            </details>
                          </>
                        )}
                      </td>
                      <td className="px-3 py-2 text-[#6d5b3f]">{job.technique}</td>
                      <td className="px-3 py-2 text-[#6d5b3f]">{job.material}</td>
                      <td className="px-3 py-2 text-[#6d5b3f]">{job.duration}s</td>
                      <td className="px-3 py-2 text-[#6d5b3f]">{LANG_LABELS[job.language] ?? job.language}</td>
                      <td className="px-3 py-2 text-[#6d5b3f]">{job.aspect}</td>
                      <td className="px-3 py-2">
                        <span className={`rounded-full px-2 py-0.5 font-bold ${statusBg[job.status]} ${statusColor[job.status]}`}>
                          {job.status === 'running' ? `${job.progress}%` : job.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-[#6d5b3f]">{job.category}</td>
                      {/* Per-job product images */}
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1 flex-wrap max-w-[110px]">
                          {(job.jobImageUrls || []).slice(0, 4).map((url, i) => (
                            <div key={i} className="relative group">
                              <img src={url} className="w-7 h-7 rounded object-cover border border-[#d6bd7c]" />
                              <button
                                onClick={() => handleJobImageRemove(job.id, i)}
                                className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-[#dc553c] text-white hidden group-hover:flex items-center justify-center"
                              >
                                <X className="w-2 h-2" />
                              </button>
                            </div>
                          ))}
                          {(job.jobImageUrls?.length || 0) > 4 && (
                            <span className="text-[9px] text-[#6d5b3f] font-bold bg-[#e9d6aa] rounded px-1">
                              +{(job.jobImageUrls?.length || 0) - 4}
                            </span>
                          )}
                          <label className="flex items-center justify-center w-7 h-7 rounded border border-dashed border-[#d6bd7c] hover:border-[#865916] hover:bg-[#fdf6e9] cursor-pointer transition-colors">
                            <ImageIcon className="w-3.5 h-3.5 text-[#a89470] hover:text-[#865916]" />
                            <input type="file" accept="image/*" multiple className="hidden"
                              onChange={e => handleJobImageAdd(job.id, e.target.files)} />
                          </label>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1">
                          {/* In script mode the job already has a script — show "Pasted" badge instead of AI script button */}
                          {job.script && job.status === 'done' && !job.review ? (
                            <span className="flex items-center gap-1 rounded px-2 py-1 text-[10px] font-bold bg-[#258cc0]/10 text-[#258cc0]">
                              <FileText className="w-3 h-3" />Pasted ✓
                            </span>
                          ) : (
                            <button
                              onClick={() => handleCreateScript(job)}
                              title="Create script with AI"
                              className="flex items-center gap-1 rounded px-2 py-1 text-[10px] font-bold bg-[#865916]/10 hover:bg-[#865916]/20 text-[#865916] transition-colors"
                            >
                              <ScrollText className="w-3 h-3" />Script
                            </button>
                          )}
                          {job.script && (
                            <button
                              onClick={() => setScriptJob(job)}
                              title="View script"
                              className="flex items-center gap-1 rounded px-2 py-1 text-[10px] font-bold bg-[#476b24]/10 hover:bg-[#476b24]/20 text-[#476b24] transition-colors"
                            >
                              <FileText className="w-3 h-3" />View
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <button
                          onClick={() => removeMpJob(job.id)}
                          className="rounded p-1 hover:bg-[#dc553c]/10 text-[#8c2d1f]/60 hover:text-[#8c2d1f] transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {/* Flowkit Skills Panel */}
          <div className="border-t border-[#d6bd7c]/40 px-4 py-3">
            <FlowkitSkillsPanel engine={mpEngine} />
          </div>
        </div>
      </div>

      {/* ── Right: Shot Status Panel (v1 only) ── */}
      <div className="master-prompt-status-panel w-64 shrink-0 flex flex-col overflow-hidden">
        <ShotStatusPanel jobId={activeJob?.id || ''} />
      </div>
      </div>
      ) : ugcMasterVersion === 'v3' ? (
        <MasterUgcV3Panel
          ugcFlowkitConfig={ugcFlowkitConfig}
          updateUgcFlowkitConfig={updateUgcFlowkitConfig}
          ugcFlowkitStarting={ugcFlowkitStarting}
          handleStartUgcFlowkit={handleStartUgcFlowkit}
          handlePreviewUgcFlowkit={handlePreviewUgcFlowkit}
          currentUgcFlowkitPreview={currentUgcFlowkitPreview}
          selectedUgcFlowkitProducts={selectedUgcFlowkitProducts}
          selectedUgcFlowkitProductIds={selectedUgcFlowkitProductIds}
          toggleUgcFlowkitProduct={toggleUgcFlowkitProduct}
          setUgcFlowkitSelectedProductIds={setUgcFlowkitSelectedProductIds}
          v2Batch={v2Batch}
          setV2Batch={setV2Batch}
          v2BatchLoading={v2BatchLoading}
          refreshV2Batch={refreshV2Batch}
          v2ActionLoading={v2ActionLoading}
          handleResumeV2Batch={handleResumeV2Batch}
          handleStopV2Batch={handleStopV2Batch}
          handleResumeProduct={handleResumeProduct}
          copiedPromptId={copiedPromptId}
          handlePromptCopy={handlePromptCopy}
          ugcFlowkitBatchId={ugcFlowkitBatchId}
          setUgcFlowkitBatchId={setUgcFlowkitBatchId}
          v2RightTab={v2RightTab}
          setV2RightTab={setV2RightTab}
          onOpenWorkspace={() => {
            if (v2Batch?.id && ugcFlowkitBatchId !== v2Batch.id) {
              setExplicitlySelectedBatchId(v2Batch.id);
              setUgcFlowkitBatchId(v2Batch.id);
            }
            setActiveTab('ugcFlowkit');
          }}
        />
      ) : (
        /* ── v2: UGC Pipeline Engine (Direct Inline Configuration & Google Flow Prompts) ── */
        <div className="ugc-pipeline-v2 flex-1 overflow-y-auto bg-[#fdf6e9] p-4 lg:p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header intro */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#d6bd7c]/50 pb-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className="rounded bg-[#865916] px-2 py-0.5 text-xs font-black text-[#fff8ec]">v2</span>
                  <h1 className="text-lg font-black text-[#2f2414]">UGC Master</h1>
                </div>
                <p className="mt-1 text-xs text-[#6d5b3f]">
                  Configure and launch dedicated product pipelines. Flowkit automates uploads and scripting; Prompt Generate Only provides ready-to-use Google Flow prompts.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => void refreshV2Batch()}
                  disabled={v2BatchLoading}
                  className="flex items-center gap-1.5 rounded-lg border border-[#d6bd7c] bg-[#fff8ec] px-3 py-1.5 text-xs font-bold text-[#6d5b3f] hover:bg-[#e9d6aa]/50 transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${v2BatchLoading ? 'animate-spin' : ''}`} />
                  Refresh Batch
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (v2Batch?.id && ugcFlowkitBatchId !== v2Batch.id) {
                      setUgcFlowkitBatchId(v2Batch.id);
                    }
                    setActiveTab('ugcFlowkit');
                  }}
                  className="flex items-center gap-1.5 rounded-lg bg-[#865916] px-3 py-1.5 text-xs font-bold text-[#fff8ec] hover:bg-[#6f4810] transition-colors"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Open Pipeline Workspace
                </button>
              </div>
            </div>

            {/* Main 2-column grid */}
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-start">
              {/* ── Left: Configuration Form ── */}
              <div className="xl:col-span-5 rounded-2xl border border-[#d6bd7c] bg-[#fff8ec] shadow-sm p-5 space-y-5">
                <div className="flex items-center justify-between border-b border-[#d6bd7c]/50 pb-3">
                  <div className="flex items-center gap-2">
                    <Settings2 className="w-4 h-4 text-[#865916]" />
                    <h2 className="text-sm font-black text-[#2f2414]">Pipeline Configuration</h2>
                  </div>
                  <span className="text-[11px] font-bold text-[#865916]">Product UGC Dedicated</span>
                </div>

                <form onSubmit={event => { event.preventDefault(); void handleStartUgcFlowkit(); }} className="space-y-4">
                  {/* Row 1: Win Products & Duration */}
                  <div className="grid grid-cols-2 gap-3">
                    <label className="text-xs font-bold text-[#6d5b3f]">
                      Win products
                      <div className="mt-1 flex gap-2">
                        <select
                          value={ugcFlowkitConfig.winProducts === 'all' ? 'all' : 'count'}
                          onChange={event => updateUgcFlowkitConfig('winProducts', event.target.value === 'all' ? 'all' : 1)}
                          className="rounded-lg border border-[#d6bd7c] bg-white px-2.5 py-1.5 text-xs font-bold text-[#2f2414] focus:outline-none focus:ring-1 focus:ring-[#865916]"
                        >
                          <option value="all">All</option>
                          <option value="count">Count</option>
                        </select>
                        {ugcFlowkitConfig.winProducts !== 'all' && (
                          <input
                            aria-label="Win product count"
                            required
                            type="number"
                            min="0"
                            max="10000"
                            value={ugcFlowkitConfig.winProducts}
                            onChange={event => updateUgcFlowkitConfig('winProducts', Number(event.target.value))}
                            className="w-full rounded-lg border border-[#d6bd7c] bg-white px-2.5 py-1.5 text-xs font-bold focus:outline-none focus:ring-1 focus:ring-[#865916]"
                          />
                        )}
                      </div>
                    </label>

                    <label className="text-xs font-bold text-[#6d5b3f]">
                      Duration (seconds)
                      <input
                        required
                        type="number"
                        min="8"
                        max="120"
                        value={ugcFlowkitConfig.durationSec}
                        onChange={event => updateUgcFlowkitConfig('durationSec', Number(event.target.value))}
                        className="mt-1 block w-full rounded-lg border border-[#d6bd7c] bg-white px-2.5 py-1.5 text-xs font-bold text-[#2f2414] focus:outline-none focus:ring-1 focus:ring-[#865916]"
                      />
                    </label>
                  </div>

                  {/* Row 2: Best & Good products */}
                  <div className="grid grid-cols-2 gap-3">
                    <label className="text-xs font-bold text-[#6d5b3f]">
                      Best products
                      <input
                        required
                        type="number"
                        min="0"
                        max="10000"
                        value={ugcFlowkitConfig.bestProducts}
                        onChange={event => updateUgcFlowkitConfig('bestProducts', Number(event.target.value))}
                        className="mt-1 block w-full rounded-lg border border-[#d6bd7c] bg-white px-2.5 py-1.5 text-xs font-bold text-[#2f2414] focus:outline-none focus:ring-1 focus:ring-[#865916]"
                      />
                    </label>
                    <label className="text-xs font-bold text-[#6d5b3f]">
                      Good products
                      <input
                        required
                        type="number"
                        min="0"
                        max="10000"
                        value={ugcFlowkitConfig.goodProducts}
                        onChange={event => updateUgcFlowkitConfig('goodProducts', Number(event.target.value))}
                        className="mt-1 block w-full rounded-lg border border-[#d6bd7c] bg-white px-2.5 py-1.5 text-xs font-bold text-[#2f2414] focus:outline-none focus:ring-1 focus:ring-[#865916]"
                      />
                    </label>
                  </div>

                  {/* Row 3: Mode & Location */}
                  <div className="grid grid-cols-2 gap-3">
                    <label className="text-xs font-bold text-[#6d5b3f]">
                      Mode
                      <select
                        value={ugcFlowkitConfig.mode}
                        onChange={event => updateUgcFlowkitConfig('mode', event.target.value as UgcFlowkitLaunchConfig['mode'])}
                        className="mt-1 block w-full rounded-lg border border-[#d6bd7c] bg-white px-2.5 py-1.5 text-xs font-bold text-[#2f2414] focus:outline-none focus:ring-1 focus:ring-[#865916]"
                      >
                        <option value="faceless">Faceless</option>
                        <option value="character">Character family</option>
                      </select>
                    </label>
                    <label className="text-xs font-bold text-[#6d5b3f]">
                      Location
                      <select
                        value={ugcFlowkitConfig.locationStrategy}
                        onChange={event => updateUgcFlowkitConfig('locationStrategy', event.target.value as UgcFlowkitLaunchConfig['locationStrategy'])}
                        className="mt-1 block w-full rounded-lg border border-[#d6bd7c] bg-white px-2.5 py-1.5 text-xs font-bold text-[#2f2414] focus:outline-none focus:ring-1 focus:ring-[#865916]"
                      >
                        <option value="generated-per-video">Generated per video</option>
                        <option value="folder">Use location folder</option>
                      </select>
                    </label>
                  </div>

                  {/* Folder Paths */}
                  <div className="space-y-3">
                    <label className="block text-xs font-bold text-[#6d5b3f]">
                      Product images folder
                      <input
                        required
                        value={ugcFlowkitConfig.productImageFolder}
                        onChange={event => updateUgcFlowkitConfig('productImageFolder', event.target.value)}
                        className="mt-1 block w-full rounded-lg border border-[#d6bd7c] bg-white px-2.5 py-1.5 text-xs font-mono text-[#2f2414] focus:outline-none focus:ring-1 focus:ring-[#865916]"
                      />
                    </label>
                    <label className="block text-xs font-bold text-[#6d5b3f]">
                      Family character folder (shared voices & portraits)
                      <input
                        required
                        value={ugcFlowkitConfig.familyCharacterFolder}
                        onChange={event => updateUgcFlowkitConfig('familyCharacterFolder', event.target.value)}
                        className="mt-1 block w-full rounded-lg border border-[#d6bd7c] bg-white px-2.5 py-1.5 text-xs font-mono text-[#2f2414] focus:outline-none focus:ring-1 focus:ring-[#865916]"
                      />
                    </label>
                    {ugcFlowkitConfig.locationStrategy === 'folder' && (
                      <label className="block text-xs font-bold text-[#6d5b3f]">
                        Location folder
                        <input
                          required
                          value={ugcFlowkitConfig.locationFolder}
                          onChange={event => updateUgcFlowkitConfig('locationFolder', event.target.value)}
                          className="mt-1 block w-full rounded-lg border border-[#d6bd7c] bg-white px-2.5 py-1.5 text-xs font-mono text-[#2f2414] focus:outline-none focus:ring-1 focus:ring-[#865916]"
                        />
                      </label>
                    )}
                  </div>

                  {/* Execution Mode Card: Prompt Generate Only & Automation */}
                  <div className="space-y-2.5">
                    {/* Prompt Generate Only Box */}
                    <div className={`rounded-xl border p-3.5 transition-all ${
                      ugcFlowkitConfig.promptOnly
                        ? 'border-[#258cc0] bg-[#258cc0]/10 shadow-sm'
                        : 'border-[#d6bd7c]/60 bg-[#fdf6e9]'
                    }`}>
                      <label className="flex items-start gap-2.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={Boolean(ugcFlowkitConfig.promptOnly)}
                          onChange={event => {
                            const checked = event.target.checked;
                            updateUgcFlowkitConfig('promptOnly', checked);
                            if (checked) {
                              updateUgcFlowkitConfig('autoGenerate', false);
                              updateUgcFlowkitConfig('autoMerge', false);
                            }
                          }}
                          className="mt-0.5 h-4 w-4 rounded accent-[#258cc0]"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-1.5 text-xs font-black text-[#176184]">
                            <Sparkles className="w-3.5 h-3.5 text-[#258cc0]" />
                            Prompt Generate Only (Google Flow manual copy-paste)
                          </div>
                          <p className="mt-1 text-[11px] text-[#6d5b3f] leading-relaxed">
                            Flowkit only uploads product gallery images to Google Flow. No Generate commands will be submitted. AI Planner generates structured prompts below for manual copy-paste.
                          </p>
                        </div>
                      </label>
                    </div>

                    {/* Automation Checkboxes */}
                    <div className="flex flex-wrap items-center gap-4 rounded-xl border border-[#d6bd7c]/60 bg-[#fdf6e9] p-3 text-xs text-[#2f2414]">
                      <label className={`flex items-center gap-2 font-bold ${ugcFlowkitConfig.promptOnly ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}>
                        <input
                          type="checkbox"
                          disabled={Boolean(ugcFlowkitConfig.promptOnly)}
                          checked={ugcFlowkitConfig.autoGenerate && !ugcFlowkitConfig.promptOnly}
                          onChange={event => updateUgcFlowkitConfig('autoGenerate', event.target.checked)}
                          className="accent-[#476b24]"
                        />
                        Auto generate
                      </label>
                      <label className={`flex items-center gap-2 font-bold ${(!ugcFlowkitConfig.autoGenerate || Boolean(ugcFlowkitConfig.promptOnly)) ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}>
                        <input
                          type="checkbox"
                          disabled={!ugcFlowkitConfig.autoGenerate || Boolean(ugcFlowkitConfig.promptOnly)}
                          checked={ugcFlowkitConfig.autoMerge && !ugcFlowkitConfig.promptOnly}
                          onChange={event => updateUgcFlowkitConfig('autoMerge', event.target.checked)}
                          className="accent-[#476b24]"
                        />
                        Auto merge
                      </label>
                      <span className="text-[10px] text-[#6d5b3f] ml-auto">
                        Planner: Codex; Gemini fallback
                      </span>
                    </div>
                  </div>

                  {/* Preview Selection */}
                  <div className="rounded-xl border border-[#865916]/30 bg-[#865916]/5 p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-[#2f2414]">Product Selection Preview</span>
                      <button
                        type="button"
                        disabled={ugcFlowkitStarting}
                        onClick={() => void handlePreviewUgcFlowkit()}
                        className="rounded-lg border border-[#865916] bg-white px-2.5 py-1 text-xs font-bold text-[#865916] hover:bg-[#865916]/10 disabled:opacity-50 transition-colors"
                      >
                        Preview selection
                      </button>
                    </div>
                    {currentUgcFlowkitPreview && (
                      <div className="rounded-lg bg-white p-2.5 text-xs text-[#2f2414] border border-[#d6bd7c]/60">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-bold text-[#476b24]">
                            Selected: {selectedUgcFlowkitProducts.length} / {currentUgcFlowkitPreview.counts.total} products
                          </p>
                          <div className="flex gap-2 text-[11px] font-bold">
                            <button type="button" onClick={() => setUgcFlowkitSelectedProductIds(currentUgcFlowkitPreview.products.map(product => product.id))} className="text-[#476b24] hover:underline">Select all</button>
                            <button type="button" onClick={() => setUgcFlowkitSelectedProductIds([])} className="text-[#8c2d1f] hover:underline">Clear</button>
                          </div>
                        </div>
                        <div className="mt-2 max-h-48 space-y-1 overflow-y-auto">
                          {currentUgcFlowkitPreview.products.map(product => (
                            <label key={product.id} className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-[#e9d6aa]/30 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedUgcFlowkitProductIds.has(product.id)}
                                onChange={event => toggleUgcFlowkitProduct(product.id, event.target.checked)}
                                className="accent-[#476b24]"
                              />
                              <span className="min-w-0 flex-1 truncate text-[11px] text-[#2f2414]">{product.name}</span>
                              <span className="text-[10px] font-bold uppercase text-[#865916]">{product.id.split(':', 1)[0]}</span>
                            </label>
                          ))}
                        </div>
                        {selectedUgcFlowkitProducts.length === 0 && (
                          <p role="alert" className="mt-2 text-[11px] font-bold text-[#8c2d1f]">Select at least one product before starting.</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Direct Start Pipeline Button */}
                  <button
                    type="submit"
                    disabled={ugcFlowkitStarting || Boolean(currentUgcFlowkitPreview && selectedUgcFlowkitProducts.length === 0)}
                    className="w-full flex items-center justify-center gap-2 rounded-xl bg-[#476b24] py-3 text-sm font-black text-[#fff8ec] hover:bg-[#395719] shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {ugcFlowkitStarting ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Validating & Queuing Pipeline…
                      </>
                    ) : (
                      <>
                        <PlayCircle className="w-5 h-5" />
                        Start Pipeline
                      </>
                    )}
                  </button>
                </form>
              </div>

              {/* ── Right: Active Batch & Google Flow Prompts Viewer OR Batches Queue ── */}
              <div className="xl:col-span-7 space-y-4">
                {/* Sub-tabs switcher */}
                <div className="flex items-center justify-between border-b border-[#d6bd7c]/60 pb-2">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setV2RightTab('prompts')}
                      className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-all ${
                        v2RightTab === 'prompts'
                          ? 'bg-[#865916] text-[#fff8ec] shadow-sm'
                          : 'text-[#6d5b3f] hover:bg-[#e9d6aa]/50 hover:text-[#2f2414]'
                      }`}
                    >
                      <Film className="w-3.5 h-3.5" />
                      Kịch bản & Prompts {v2Batch ? `(${v2Batch.id.slice(0, 8)}…)` : ''}
                    </button>
                    <button
                      type="button"
                      onClick={() => setV2RightTab('batches')}
                      className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-all ${
                        v2RightTab === 'batches'
                          ? 'bg-[#865916] text-[#fff8ec] shadow-sm'
                          : 'text-[#6d5b3f] hover:bg-[#e9d6aa]/50 hover:text-[#2f2414]'
                      }`}
                    >
                      <History className="w-3.5 h-3.5" />
                      Batches & Hàng đợi
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (v2Batch?.id && ugcFlowkitBatchId !== v2Batch.id) {
                        setUgcFlowkitBatchId(v2Batch.id);
                      }
                      setActiveTab('ugcFlowkit');
                    }}
                    className="flex items-center gap-1 text-xs font-bold text-[#865916] hover:underline"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Workspace chi tiết
                  </button>
                </div>

                {v2RightTab === 'batches' ? (
                  <UgcBatchesQueueView
                    selectedBatchId={v2Batch?.id || ugcFlowkitBatchId}
                    onSelectBatch={async (batchId) => {
                      setExplicitlySelectedBatchId(batchId);
                      setUgcFlowkitBatchId(batchId);
                      setV2RightTab('prompts');
                      try {
                        const loaded = await getUgcFlowkitBatch(batchId);
                        setV2Batch(loaded);
                      } catch {
                        // fallback
                      }
                    }}
                    onActionComplete={() => {
                      void refreshV2Batch();
                    }}
                  />
                ) : (
                  v2Batch ? (
                  <>
                    {/* Active Batch Summary Header */}
                    <div className="rounded-2xl border border-[#d6bd7c] bg-[#fff8ec] p-4 shadow-sm flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <Film className="w-4 h-4 text-[#865916]" />
                          <span className="text-xs font-mono font-bold text-[#865916]">Batch {v2Batch.id}</span>
                          <span className="rounded-full bg-[#76a441]/20 px-2 py-0.5 text-[10px] font-bold text-[#476b24]">
                            {v2Batch.products.length} Products
                          </span>
                          <span className="rounded-full bg-[#865916]/10 px-2 py-0.5 text-[10px] font-bold text-[#865916]">
                            {v2Batch.config.mode}
                          </span>
                          {v2Batch.isQueued && (
                            <span className="inline-flex items-center gap-1 rounded-full border border-amber-400 bg-amber-100 px-2 py-0.5 text-[10px] font-black text-amber-800">
                              <Clock className="w-3 h-3" /> Hàng đợi #{v2Batch.queuePosition || 1}
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-[11px] text-[#6d5b3f]">
                          Started: {new Date(v2Batch.createdAt).toLocaleString()} · Duration: {v2Batch.config.durationSec}s
                        </p>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => void refreshV2Batch()}
                          disabled={v2BatchLoading}
                          className="flex items-center gap-1 rounded-lg border border-[#d6bd7c] px-2.5 py-1 text-xs font-bold text-[#6d5b3f] hover:bg-[#e9d6aa]/50"
                        >
                          <RefreshCw className={`w-3 h-3 ${v2BatchLoading ? 'animate-spin' : ''}`} />
                          Refresh
                        </button>
                      </div>
                    </div>

                    {/* Batch Blocked Banner */}
                    {v2Batch.runtime?.blocked && (
                      <div className="rounded-xl border border-red-400 bg-red-50 p-4 text-red-900 shadow-sm">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div className="flex items-start gap-2.5">
                            <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                            <div>
                              <div className="font-bold text-sm">
                                Batch đang tạm dừng: {v2Batch.runtime.blocked.code}
                              </div>
                              <div className="text-xs text-red-700 mt-1 font-mono break-all">
                                {v2Batch.runtime.blocked.message}
                              </div>
                              {v2Batch.runtime.blocked.at && (
                                <div className="text-[11px] text-red-600 mt-1">
                                  Thời gian: {new Date(v2Batch.runtime.blocked.at).toLocaleString()}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <button
                              type="button"
                              disabled={Boolean(v2ActionLoading)}
                              onClick={handleResumeV2Batch}
                              className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-700 transition-colors shadow-sm disabled:opacity-50 flex items-center gap-1.5"
                            >
                              <PlayCircle className="w-3.5 h-3.5" />
                              {v2ActionLoading === 'resume-batch' ? 'Đang tiếp tục...' : 'Tiếp tục chạy (Resume)'}
                            </button>
                            <button
                              type="button"
                              disabled={Boolean(v2ActionLoading)}
                              onClick={handleStopV2Batch}
                              className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-red-700 transition-colors shadow-sm disabled:opacity-50 flex items-center gap-1.5"
                            >
                              <StopCircle className="w-3.5 h-3.5" />
                              {v2ActionLoading === 'stop-batch' ? 'Đang dừng...' : 'Dừng hẳn batch'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Queue Notification Banner if Queued */}
                    {v2Batch.isQueued && (
                      <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-900 shadow-sm">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div className="flex items-center gap-2.5">
                            <Clock className="w-5 h-5 text-amber-600 shrink-0" />
                            <div>
                              <div className="font-bold text-sm">
                                Batch đang chờ trong Hàng đợi (Vị trí #{v2Batch.queuePosition || 1})
                              </div>
                              <div className="text-xs text-amber-700 mt-0.5">
                                Hệ thống đang xử lý batch trước đó. Batch này sẽ tự động khởi chạy và phân tích sản phẩm ngay khi tới lượt.
                              </div>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setV2RightTab('batches')}
                            className="rounded-lg bg-amber-200/90 px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-300 transition-colors shrink-0"
                          >
                            Xem toàn bộ hàng đợi
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Mode Banner if Prompt Generate Only */}
                    {v2Batch.config.promptOnly && (
                      <div className="rounded-xl border border-[#258cc0]/40 bg-[#258cc0]/10 p-4">
                        <div className="flex items-center gap-2 text-xs font-black text-[#176184]">
                          <Sparkles className="w-4 h-4 text-[#258cc0]" />
                          Prompt Generate Only Mode Active
                        </div>
                        <p className="mt-1 text-xs text-[#6d5b3f]">
                          Product gallery images have been uploaded to Google Flow without sending automated generation commands.
                          Use the <strong>1-click copy buttons</strong> below to copy Location Reference, Scene Frame-0, Video Motion, and VO prompts into Google Flow.
                        </p>
                      </div>
                    )}

                    {/* Products Prompt Cards */}
                    <div className="space-y-4">
                      {v2Batch.products.map(product => {
                        const plan = product.runtime?.plan;
                        const locationPrompt = product.runtime?.productVision?.locationPrompt || plan?.location?.name || '';
                        const isStoppedSafe = product.status === 'stopped_safe';
                        const isPlanning = (product.status === 'planning' || product.runtime?.phase === 'planning') && !isStoppedSafe && !v2Batch.runtime?.blocked;
                        const isQueued = product.status === 'queued';

                        return (
                          <div key={product.id} className="rounded-2xl border border-[#d6bd7c] bg-[#fff8ec] shadow-sm overflow-hidden">
                            {/* Product Header */}
                            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#d6bd7c]/50 bg-[#f4e8c9]/50 px-4 py-3">
                              <div>
                                <h3 className="text-sm font-black text-[#2f2414]">{product.name}</h3>
                                <div className="mt-0.5 flex items-center gap-2 text-[11px] text-[#6d5b3f]">
                                  <span>Status: <strong className="capitalize">{product.status}</strong></span>
                                  {product.runtime?.phase && <span>· Phase: {product.runtime.phase}</span>}
                                  {product.runtime?.planner && <span>· Planner: {product.runtime.planner}</span>}
                                </div>
                              </div>

                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => handlePromptCopy(formatGoogleFlowPrompts(product), `all-${product.id}`)}
                                  className="flex items-center gap-1.5 rounded-lg bg-[#258cc0] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#1f739e] transition-colors shadow-sm"
                                >
                                  {copiedPromptId === `all-${product.id}` ? (
                                    <>
                                      <Check className="w-3.5 h-3.5" />
                                      Copied All!
                                    </>
                                  ) : (
                                    <>
                                      <Copy className="w-3.5 h-3.5" />
                                      Copy All Google Flow Prompts
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>

                            {/* Product Body */}
                            <div className="p-4 space-y-4">
                              {/* Stopped Safe indicator & recovery */}
                              {isStoppedSafe && (
                                <div className="rounded-xl border border-amber-300 bg-amber-50/80 p-3.5 text-xs text-amber-900 space-y-2">
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-start gap-2">
                                      <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                                      <div>
                                        <span className="font-bold text-amber-800">
                                          Pipeline dừng an toàn (Safe Stop): {product.safeStop?.code || 'STOPPED'}
                                        </span>
                                        {product.safeStop?.message && (
                                          <p className="mt-1 text-amber-700 break-words font-mono text-[11px]">
                                            {product.safeStop.message}
                                          </p>
                                        )}
                                        {product.safeStop?.at && (
                                          <div className="text-[10px] text-amber-600 mt-0.5">
                                            Thời gian: {new Date(product.safeStop.at).toLocaleString()}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      disabled={Boolean(v2ActionLoading)}
                                      onClick={() => handleResumeProduct(product.id)}
                                      className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-amber-700 transition-colors shadow-sm shrink-0 flex items-center gap-1.5 disabled:opacity-50"
                                    >
                                      <RefreshCw className={`w-3.5 h-3.5 ${v2ActionLoading === `resume-${product.id}` ? 'animate-spin' : ''}`} />
                                      {v2ActionLoading === `resume-${product.id}` ? 'Đang thử lại...' : 'Thử lại (Retry)'}
                                    </button>
                                  </div>
                                </div>
                              )}

                              {/* Queue indicator */}
                              {isQueued && (
                                <div className="flex items-center gap-3 rounded-xl border border-amber-300/80 bg-amber-50 p-3 text-xs text-amber-800">
                                  <Clock className="w-4 h-4 text-amber-600 shrink-0" />
                                  <span>Đang trong hàng đợi — Sẽ bắt đầu phân tích hình ảnh và tạo prompt khi batch được kích hoạt.</span>
                                </div>
                              )}

                              {/* Planning indicator */}
                              {isPlanning && !isQueued && (
                                <div className="flex items-center gap-3 rounded-xl border border-[#258cc0]/30 bg-[#258cc0]/10 p-3 text-xs text-[#176184]">
                                  <RefreshCw className="w-4 h-4 animate-spin text-[#258cc0] shrink-0" />
                                  <span>AI Planner đang phân tích hình ảnh sản phẩm và tạo bộ prompts…</span>
                                </div>
                              )}

                              {/* Merged Video Player */}
                              {product.runtime?.merged?.url && (
                                <div className="rounded-xl border border-[#76a441]/50 bg-[#76a441]/10 p-3 flex flex-wrap items-center justify-between gap-3">
                                  <div className="flex items-center gap-3">
                                    <video
                                      controls
                                      preload="metadata"
                                      src={product.runtime.merged.url}
                                      className="w-28 rounded-lg border border-[#d6bd7c] bg-black shadow-sm"
                                    />
                                    <div className="text-xs space-y-1">
                                      <div className="font-black text-[#476b24] flex items-center gap-1.5">
                                        <CheckCircle2 className="w-4 h-4" />
                                        Video hoàn chỉnh ({product.runtime.merged.clipCount} clips)
                                      </div>
                                      <div className="text-[11px] text-[#6d5b3f]">
                                        Hoàn tất: {new Date(product.runtime.merged.completedAt).toLocaleString()}
                                      </div>
                                      <a
                                        href={product.runtime.merged.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-1 rounded-lg bg-[#476b24] px-2.5 py-1 text-[11px] font-bold text-[#fff8ec] hover:bg-[#395719] transition-colors"
                                      >
                                        <ExternalLink className="w-3 h-3" />
                                        Mở / Tải Video
                                      </a>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Hook & Concept */}
                              {plan && (
                                <div className="rounded-xl border border-[#d6bd7c]/60 bg-[#fdf6e9] p-3 text-xs space-y-1">
                                  <div className="font-bold text-[#865916]">Hook: <span className="font-normal text-[#2f2414]">{plan.hook}</span></div>
                                  <div className="font-bold text-[#865916]">Concept: <span className="font-normal text-[#2f2414]">{plan.concept}</span></div>
                                  {plan.voiceRole && (
                                    <div className="text-[11px] text-[#6d5b3f]">Voice: <span className="capitalize font-bold text-[#2f2414]">{plan.voiceRole}</span></div>
                                  )}
                                </div>
                              )}

                              {/* 1. Location Reference Prompt */}
                              {locationPrompt && (
                                <div className="rounded-xl border border-[#865916]/30 bg-white p-3.5 space-y-2">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-black text-[#865916] uppercase tracking-wide">
                                      1. Reference (Location) Prompt
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => handlePromptCopy(locationPrompt, `loc-${product.id}`)}
                                      className="flex items-center gap-1 rounded border border-[#d6bd7c] bg-[#fff8ec] px-2 py-0.5 text-[11px] font-bold text-[#6d5b3f] hover:bg-[#e9d6aa]/50 transition-colors"
                                    >
                                      {copiedPromptId === `loc-${product.id}` ? (
                                        <span className="text-[#476b24] flex items-center gap-1"><Check className="w-3 h-3" />Copied!</span>
                                      ) : (
                                        <span className="flex items-center gap-1"><Copy className="w-3 h-3" />Copy Reference Prompt</span>
                                      )}
                                    </button>
                                  </div>
                                  <div className="rounded border border-[#d6bd7c]/40 bg-[#fdf6e9] p-2.5 text-xs text-[#2f2414] font-mono whitespace-pre-wrap select-all">
                                    {locationPrompt}
                                  </div>
                                </div>
                              )}

                              {/* 2. Scenes Prompts */}
                              {plan?.scenes && plan.scenes.length > 0 && (
                                <div className="space-y-3">
                                  <div className="text-xs font-black text-[#865916] uppercase tracking-wide">
                                    2. Scenes (Frame-0 Still, Motion & VO)
                                  </div>
                                  {plan.scenes.map(scene => (
                                    <div key={scene.number} className="rounded-xl border border-[#d6bd7c]/60 bg-white p-3 space-y-2.5">
                                      <div className="flex items-center justify-between border-b border-[#d6bd7c]/40 pb-1.5">
                                        <span className="text-xs font-black text-[#2f2414]">
                                          Scene {scene.number} ({scene.retainDurationSec}s) — Source: {scene.sourceReferenceName}
                                        </span>
                                      </div>

                                      {/* Frame-0 Prompt */}
                                      <div className="space-y-1">
                                        <div className="flex items-center justify-between text-[11px]">
                                          <span className="font-bold text-[#6d5b3f]">Frame-0 Still Prompt:</span>
                                          <button
                                            type="button"
                                            onClick={() => handlePromptCopy(scene.prompt, `scene-${product.id}-${scene.number}`)}
                                            className="text-[10px] font-bold text-[#258cc0] hover:underline flex items-center gap-1"
                                          >
                                            {copiedPromptId === `scene-${product.id}-${scene.number}` ? (
                                              <span className="text-[#476b24]">Copied ✓</span>
                                            ) : (
                                              <span>Copy Scene Prompt</span>
                                            )}
                                          </button>
                                        </div>
                                        <div className="rounded border border-[#d6bd7c]/40 bg-[#fdf6e9] p-2 text-xs font-mono text-[#2f2414] whitespace-pre-wrap select-all">
                                          {scene.prompt}
                                        </div>
                                      </div>

                                      {/* Video Motion Prompt */}
                                      <div className="space-y-1">
                                        <div className="flex items-center justify-between text-[11px]">
                                          <span className="font-bold text-[#6d5b3f]">8-Second Video Motion Prompt:</span>
                                          <button
                                            type="button"
                                            onClick={() => handlePromptCopy(scene.videoPrompt, `vid-${product.id}-${scene.number}`)}
                                            className="text-[10px] font-bold text-[#258cc0] hover:underline flex items-center gap-1"
                                          >
                                            {copiedPromptId === `vid-${product.id}-${scene.number}` ? (
                                              <span className="text-[#476b24]">Copied ✓</span>
                                            ) : (
                                              <span>Copy Video Prompt</span>
                                            )}
                                          </button>
                                        </div>
                                        <div className="rounded border border-[#d6bd7c]/40 bg-[#fdf6e9] p-2 text-xs font-mono text-[#2f2414] whitespace-pre-wrap select-all">
                                          {scene.videoPrompt}
                                        </div>
                                      </div>

                                      {/* Voiceover */}
                                      <div className="space-y-1">
                                        <div className="flex items-center justify-between text-[11px]">
                                          <span className="font-bold text-[#6d5b3f]">Voiceover Script:</span>
                                          <button
                                            type="button"
                                            onClick={() => handlePromptCopy(`${scene.voiceover}${scene.babyInterjection ? ` (${scene.babyInterjection})` : ''}`, `vo-${product.id}-${scene.number}`)}
                                            className="text-[10px] font-bold text-[#258cc0] hover:underline flex items-center gap-1"
                                          >
                                            {copiedPromptId === `vo-${product.id}-${scene.number}` ? (
                                              <span className="text-[#476b24]">Copied ✓</span>
                                            ) : (
                                              <span>Copy VO</span>
                                            )}
                                          </button>
                                        </div>
                                        <div className="rounded border border-[#d6bd7c]/40 bg-[#fdf6e9] p-2 text-xs text-[#2f2414] select-all">
                                          {scene.voiceover}
                                          {scene.babyInterjection && (
                                            <span className="ml-1 text-[#865916] italic">({scene.babyInterjection})</span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  /* Empty state when no batch has run yet */
                  <div className="rounded-2xl border border-dashed border-[#d6bd7c] bg-[#fff8ec] p-8 text-center space-y-3">
                    <Sparkles className="w-10 h-10 text-[#d6bd7c] mx-auto" />
                    <h3 className="text-sm font-black text-[#2f2414]">No Active Pipeline Batch</h3>
                    <p className="text-xs text-[#6d5b3f] max-w-md mx-auto leading-relaxed">
                      Configure your pipeline settings on the left and click <strong>Start Pipeline</strong>.
                      In <strong>Prompt Generate Only</strong> mode, Flowkit uploads product images to Google Flow and generates full prompts right here for manual execution.
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Script Output Panel (modal) ── */}
      {scriptJob && (
        <ScriptOutputPanel
          key={scriptJob.id}
          job={scriptJob}
          provider={mpCliProvider}
          engine={mpEngine}
          onClose={() => setScriptJob(null)}
          onJobUpdate={handleJobUpdate}
        />
      )}
    </div>
  );
}
